import { useEffect, useMemo, useState } from "react";

const NAV = [
  { key: "dashboard", label: "Dashboard" },
  { key: "officer-safety", label: "Officer Safety" },
  { key: "intel", label: "Regional Feed" },
  { key: "create", label: "Create Information" },
  { key: "targets", label: "Subject Profiles" },
  { key: "operations", label: "Operations Board" },
  { key: "map", label: "Regional Map" },
  { key: "timeline", label: "Activity Timeline" },
  { key: "regions", label: "Regions" },
  { key: "messages", label: "Messages" },
  { key: "admin", label: "Admin" },
  { key: "audit", label: "Audit Log" },
];

const POST_TYPES = [
  "BOLO",
  "Information Release",
  "Officer Safety Alert",
  "Attempt to Locate",
  "Suspicious Activity",
  "Drug Info",
  "Other Crime Info",
  "Information Bulletin",
];

const PRIORITIES = ["Low", "Normal", "High", "Critical"];

const SAFETY_ALERT_TYPES = [
  "Officer Safety",
  "Armed & Dangerous",
  "Known Weapons",
  "Violent History",
  "Narcotics Trafficking",
  "Tactical Risk",
  "Critical BOLO",
  "Flee / Elude Risk",
];

const THREAT_FLAGS = [
  "Armed & Dangerous",
  "Known to Flee",
  "Narcotics Distributor",
  "Violent History",
  "Officer Safety Hazard",
  "Gang Affiliation",
  "Tactical Consideration",
  "History of Weapons",
];

const TARGET_STORAGE_KEY = "spear_target_profiles_v1";
const OPS_STORAGE_KEY = "spear_operations_board_v1";
const DISCLOSURE_STORAGE_KEY = "spear_user_disclosure_ack_v1";
const AUDIT_STORAGE_KEY = "spear_audit_log_v1";
const TIMELINE_STORAGE_KEY = "spear_activity_timeline_v1";
const ALERT_SUBSCRIPTION_STORAGE_KEY = "spear_alert_subscriptions_v1";
const INTEL_STORAGE_KEY = "spear_information_posts_v1";
const REGIONS_STORAGE_KEY = "spear_regions_v1";
const DASHBOARD_SEEN_STORAGE_KEY = "spear_dashboard_seen_v1";
const OFFICER_SAFETY_STORAGE_KEY = "spear_officer_safety_alerts_v1";
const ADMIN_PROFILE_STORAGE_KEY = "spear_admin_profile_v1";

const DEFAULT_ALERT_REGIONS = ["Saguache", "Rio Grande", "Alamosa", "Conejos"];

const DEFAULT_REGIONS = [
  { id: "SAGUACHE", name: "Saguache", description: "Saguache County regional sharing group", member_count: 0 },
  { id: "RIO_GRANDE", name: "Rio Grande", description: "Rio Grande County regional sharing group", member_count: 0 },
  { id: "ALAMOSA", name: "Alamosa", description: "Alamosa County regional sharing group", member_count: 0 },
  { id: "CONEJOS", name: "Conejos", description: "Conejos County regional sharing group", member_count: 0 },
];

const DEFAULT_AGENCIES = [
  { id: "SCSO", name: "Saguache County Sheriff's Office", short_name: "SCSO" },
  { id: "RGSO", name: "Rio Grande County Sheriff's Office", short_name: "RGSO" },
  { id: "ACSO", name: "Alamosa County Sheriff's Office", short_name: "ACSO" },
  { id: "CCSO", name: "Conejos County Sheriff's Office", short_name: "CCSO" },
  { id: "FAST", name: "Regional FAST Team", short_name: "FAST" },
];

const OP_STATUSES = ["Planning", "Active", "Completed", "Archived"];

async function api(url, options = {}) {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "content-type": "application/json", ...(options.headers || {}) },
    ...options,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Request failed");
  return data;
}

function loadLocalTargets() {
  try {
    const raw = localStorage.getItem(TARGET_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(normalizeTargetProfile) : [];
  } catch {
    return [];
  }
}

function saveLocalTargets(targets) {
  localStorage.setItem(TARGET_STORAGE_KEY, JSON.stringify(targets));
}

function loadLocalOps() {
  try {
    const raw = localStorage.getItem(OPS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocalOps(ops) {
  localStorage.setItem(OPS_STORAGE_KEY, JSON.stringify(ops));
}

function loadLocalAudit() {
  try {
    return JSON.parse(localStorage.getItem(AUDIT_STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveLocalAudit(logs) {
  try {
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(logs));
  } catch {}
}

function loadLocalTimeline() {
  try {
    const raw = localStorage.getItem(TIMELINE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocalTimeline(events) {
  try {
    localStorage.setItem(TIMELINE_STORAGE_KEY, JSON.stringify(events));
  } catch {}
}

function loadLocalIntel() {
  try {
    const raw = localStorage.getItem(INTEL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocalIntel(posts) {
  try {
    localStorage.setItem(INTEL_STORAGE_KEY, JSON.stringify(posts));
  } catch {}
}

function loadLocalOfficerSafetyAlerts() {
  try {
    const raw = localStorage.getItem(OFFICER_SAFETY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocalOfficerSafetyAlerts(alerts) {
  try {
    localStorage.setItem(OFFICER_SAFETY_STORAGE_KEY, JSON.stringify(alerts));
  } catch {}
}

function getOfficerSafetyRegions(alert) {
  const explicit = ensureArray(alert?.regions);
  if (explicit.length) return explicit;
  const text = [alert?.title, alert?.subjectName, alert?.summary, alert?.location, alert?.vehicle].filter(Boolean).join(" ").toLowerCase();
  const counties = new Set();
  MAP_LOCATIONS.forEach((place) => {
    if (text.includes(place.name.toLowerCase()) || text.includes(place.county.toLowerCase())) counties.add(place.county);
  });
  return Array.from(counties);
}

function loadLocalRegions() {
  try {
    const raw = localStorage.getItem(REGIONS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const custom = Array.isArray(parsed) ? parsed : [];
    const byId = new Map([...DEFAULT_REGIONS, ...custom].map((r) => [String(r.id || r.name || makeId("region")).toUpperCase(), r]));
    return Array.from(byId.values());
  } catch {
    return DEFAULT_REGIONS;
  }
}

function saveLocalRegions(regions) {
  try {
    const custom = ensureArray(regions).filter((r) => !DEFAULT_REGIONS.some((d) => d.id === r?.id));
    localStorage.setItem(REGIONS_STORAGE_KEY, JSON.stringify(custom));
  } catch {}
}


function loadSeenDashboardAlerts() {
  try {
    const raw = localStorage.getItem(DASHBOARD_SEEN_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSeenDashboardAlerts(ids) {
  try {
    localStorage.setItem(DASHBOARD_SEEN_STORAGE_KEY, JSON.stringify(ids));
  } catch {}
}

function textMatchesRegion(value, regionName) {
  return String(value || "").toLowerCase().includes(String(regionName || "").toLowerCase());
}

function getLocationRegionFromText(value) {
  const text = String(value || "").toLowerCase();
  const match = MAP_LOCATIONS.find((place) => {
    return text.includes(place.name.toLowerCase()) || text.includes(place.county.toLowerCase());
  });
  return match?.county || "";
}


function loadAlertSubscriptions() {
  try {
    const raw = localStorage.getItem(ALERT_SUBSCRIPTION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : ["Saguache", "Rio Grande"];
  } catch {
    return ["Saguache", "Rio Grande"];
  }
}

function saveAlertSubscriptions(regions) {
  try {
    localStorage.setItem(ALERT_SUBSCRIPTION_STORAGE_KEY, JSON.stringify(regions));
  } catch {}
}

function getPostRegionNames(item, regions = []) {
  const names = new Set();
  const regionById = new Map((regions || []).map((r) => [String(r.id || r.name || "").toLowerCase(), r.name || r.id]));

  if (Array.isArray(item?.targets)) {
    item.targets.forEach((target) => {
      const targetType = String(target.target_type || "").toLowerCase();
      const targetId = String(target.target_id || "").toLowerCase();
      if (targetType === "region" || targetType === "county") {
        names.add(regionById.get(targetId) || target.target_id);
      }
    });
  }

  const haystack = [item?.title, item?.summary, item?.body, item?.created_by_agency, item?.agency_id]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  DEFAULT_ALERT_REGIONS.forEach((county) => {
    if (haystack.includes(county.toLowerCase())) names.add(county);
  });

  return Array.from(names).filter(Boolean);
}

function isAlertWorthy(item) {
  // Subscribed regions should surface every regional information item, regardless of risk level.
  return Boolean(item);
}

function makeId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function splitLines(value) {
  return String(value || "")
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);
}


function formatDateTime(value) {
  if (!value) return "Unknown date/time";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch {
    return String(value || "Unknown date/time");
  }
}

function ensureArray(value) {
  if (Array.isArray(value)) return value.filter((x) => x !== null && x !== undefined);
  if (value === null || value === undefined || value === "") return [];
  if (typeof value === "string") {
    return value
      .split(/\n|,/)
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [String(value)];
}

function textValue(value) {
  if (Array.isArray(value)) return value.join("\n");
  if (value === null || value === undefined) return "";
  return String(value);
}

function safeIncludes(collection, value) {
  try {
    return ensureArray(collection).includes(value);
  } catch {
    return false;
  }
}

function safeSome(collection, predicate) {
  try {
    return ensureArray(collection).some(predicate);
  } catch {
    return false;
  }
}

function safeFilter(collection, predicate) {
  try {
    return ensureArray(collection).filter(predicate);
  } catch {
    return [];
  }
}

function safeMap(collection, mapper) {
  try {
    return ensureArray(collection).map(mapper);
  } catch {
    return [];
  }
}

function safeForEach(collection, callback) {
  try {
    ensureArray(collection).forEach(callback);
  } catch {}
}

function safeIdSelected(container, field, item) {
  try {
    const id = item?.id;
    if (!id) return false;
    return ensureArray(container?.[field]).includes(id);
  } catch {
    return false;
  }
}

function normalizeTargetProfile(target = {}) {
  return {
    ...target,
    id: target.id || makeId("target"),
    name: target.name || target.title || "Unnamed Subject",
    aliases: ensureArray(target.aliases),
    identifiers: ensureArray(target.identifiers),
    vehicles: ensureArray(target.vehicles),
    associated_regions: ensureArray(target.associated_regions),
    associated_towns: ensureArray(target.associated_towns),
    addresses: ensureArray(target.addresses),
    associates: ensureArray(target.associates),
    flags: ensureArray(target.flags),
    edit_history: ensureArray(target.edit_history),
    status: target.status || "Active",
    threat_level: target.threat_level || target.priority || "Normal",
    summary: target.summary || "",
    notes: target.notes || "",
  };
}



function safeReactNode(value) {
  if (value === null || value === undefined || typeof value === "boolean") return null;
  if (typeof value === "string" || typeof value === "number") return value;
  if (Array.isArray(value)) return value.map((item, index) => <span key={index}>{safeReactNode(item)}</span>);
  if (typeof value === "object" && value.$$typeof) return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}


const ATTACHMENT_MAX_FILES = 6;
const ATTACHMENT_MAX_SIZE_MB = 8;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Unable to read file."));
    reader.readAsDataURL(file);
  });
}

async function prepareAttachmentFiles(fileList) {
  const files = Array.from(fileList || []);
  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
  const prepared = [];

  for (const file of files.slice(0, ATTACHMENT_MAX_FILES)) {
    if (!allowedTypes.includes(file.type)) {
      throw new Error("Only JPG, PNG, WEBP, GIF, and PDF attachments are supported right now.");
    }

    const maxBytes = ATTACHMENT_MAX_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error(`Attachment "${file.name}" is too large. Max size is ${ATTACHMENT_MAX_SIZE_MB} MB.`);
    }

    const dataUrl = await readFileAsDataUrl(file);
    prepared.push({
      id: makeId("attachment"),
      name: file.name,
      type: file.type,
      size: file.size,
      dataUrl,
      createdAt: new Date().toISOString(),
    });
  }

  return prepared;
}

function formatFileSize(bytes) {
  const value = Number(bytes || 0);
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${value} B`;
}

function attachmentCountLabel(attachments) {
  const count = ensureArray(attachments).length;
  return `${count} attachment${count === 1 ? "" : "s"}`;
}

function AttachmentViewer({ file, onClose }) {
  if (!file) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(0,0,0,.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(900px, 96vw)",
          maxHeight: "90vh",
          background: "#111820",
          border: "1px solid #33465a",
          borderRadius: 16,
          padding: 14,
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 900 }}>{file?.name || "Attachment"}</div>
            <div style={{ color: "#8fa2b5", fontSize: 12 }}>{formatFileSize(file?.size)}</div>
          </div>
          <button type="button" style={primaryButtonStyle} onClick={onClose}>Close</button>
        </div>

        {String(file?.type || "").startsWith("image/") ? (
          <img
            src={file?.dataUrl}
            alt={file?.name || "Attachment"}
            style={{ maxWidth: "100%", maxHeight: "74vh", display: "block", margin: "0 auto", borderRadius: 12 }}
          />
        ) : (
          <iframe title={file?.name || "PDF Attachment"} src={file?.dataUrl} style={{ width: "100%", height: "74vh", border: 0, borderRadius: 12 }} />
        )}
      </div>
    </div>
  );
}

function AttachmentManager({ attachments, onChange, label = "Attachments / Photos" }) {
  const safeAttachments = ensureArray(attachments);
  const [viewer, setViewer] = useState(null);

  async function handleFiles(e) {
    try {
      const selected = await prepareAttachmentFiles(e.target.files);
      const next = [...safeAttachments, ...selected].slice(0, ATTACHMENT_MAX_FILES);
      onChange(next);
      e.target.value = "";
    } catch (err) {
      alert(err?.message || "Unable to add attachment.");
      e.target.value = "";
    }
  }

  function removeAttachment(id) {
    onChange(safeAttachments.filter((item) => item?.id !== id));
  }

  return (
    <div style={{ marginTop: 12 }}>
      <Label>{label}</Label>
      <div style={{ border: "1px dashed #3b4c5f", borderRadius: 14, padding: 14, background: "#101820" }}>
        <input type="file" multiple accept="image/*,.pdf" onChange={handleFiles} style={{ color: "#dce6ef", width: "100%" }} />
        <div style={{ color: "#8fa2b5", fontSize: 12, marginTop: 8 }}>
          Up to {ATTACHMENT_MAX_FILES} files. JPG, PNG, WEBP, GIF, or PDF. Max {ATTACHMENT_MAX_SIZE_MB} MB each.
        </div>

        {safeAttachments.length ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 12 }}>
            {safeAttachments.map((file) => (
              <div key={file?.id || file?.name} style={{ border: "1px solid #273646", borderRadius: 12, overflow: "hidden", background: "#151f29" }}>
                {String(file?.type || "").startsWith("image/") ? (
                  <button type="button" onClick={() => setViewer(file)} style={{ width: "100%", height: 115, border: 0, padding: 0, background: "#0d1218", cursor: "pointer" }}>
                    <img src={file?.dataUrl} alt={file?.name || "Attachment"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </button>
                ) : (
                  <div style={{ height: 115, display: "flex", alignItems: "center", justifyContent: "center", color: "#dce6ef", fontWeight: 900, background: "#0d1218" }}>PDF</div>
                )}

                <div style={{ padding: 10 }}>
                  <div style={{ fontWeight: 800, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file?.name || "Attachment"}</div>
                  <div style={{ color: "#8fa2b5", fontSize: 11, marginTop: 3 }}>{formatFileSize(file?.size)}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button type="button" onClick={() => setViewer(file)} style={{ ...secondaryButtonStyle, padding: "7px 9px", fontSize: 12 }}>View</button>
                    <button type="button" onClick={() => removeAttachment(file?.id)} style={{ ...secondaryButtonStyle, padding: "7px 9px", fontSize: 12 }}>Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <AttachmentViewer file={viewer} onClose={() => setViewer(null)} />
    </div>
  );
}

function AttachmentPreviewStrip({ attachments }) {
  const safeAttachments = ensureArray(attachments);
  const [viewer, setViewer] = useState(null);
  if (!safeAttachments.length) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ color: "#9eb0c3", fontSize: 12, fontWeight: 900, marginBottom: 8 }}>
        {attachmentCountLabel(safeAttachments)}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {safeAttachments.slice(0, 6).map((file) => (
          <button
            type="button"
            key={file?.id || file?.name}
            onClick={() => setViewer(file)}
            title={file?.name || "Attachment"}
            style={{
              width: 76,
              height: 64,
              borderRadius: 10,
              border: "1px solid #33465a",
              background: "#0d1218",
              color: "#dce6ef",
              overflow: "hidden",
              cursor: "pointer",
              padding: 0,
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            {String(file?.type || "").startsWith("image/") ? (
              <img src={file?.dataUrl} alt={file?.name || "Attachment"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              "PDF"
            )}
          </button>
        ))}
      </div>

      <AttachmentViewer file={viewer} onClose={() => setViewer(null)} />
    </div>
  );
}

function Badge({ children, tone = "default" }) {
  const colors = {
    default: "#2b3440",
    low: "#355c3a",
    normal: "#2e4158",
    high: "#7a5521",
    critical: "#7a2222",
    warning: "#6b4b19",
    target: "#3b2d66",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        background: colors[tone] || colors.default,
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {safeReactNode(children)}
    </span>
  );
}

function Card({ children, accent = false }) {
  return (
    <div
      style={{
        background: accent ? "linear-gradient(135deg, #151a20, #171d29)" : "#151a20",
        border: accent ? "1px solid #36506b" : "1px solid #26313d",
        borderRadius: 14,
        padding: 16,
        marginBottom: 14,
        boxShadow: accent ? "0 10px 28px rgba(0,0,0,.22)" : "none",
      }}
    >
      {safeReactNode(children)}
    </div>
  );
}

function SectionTitle({ title, subtitle, right }) {
  const safeTitle = typeof title === "string" || typeof title === "number" ? title : "";
  const safeSubtitle = typeof subtitle === "string" || typeof subtitle === "number" ? subtitle : "";

  return (
    <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", gap: 14 }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{safeTitle}</div>
        {safeSubtitle ? (
          <div style={{ fontSize: 13, color: "#9eb0c3", marginTop: 4 }}>{safeSubtitle}</div>
        ) : null}
      </div>
      {right ? <div>{safeReactNode(right)}</div> : null}
    </div>
  );
}

const emptyTargetForm = {
  name: "",
  aliases: "",
  status: "Active",
  threat_level: "Normal",
  summary: "",
  identifiers: "",
  vehicles: "",
  associated_regions: [],
  associated_towns: [],
  addresses: "",
  associates: "",
  flags: [],
  notes: "",
  attachments: [],
};

const emptyOperationForm = {
  title: "",
  status: "Planning",
  priority: "High",
  objective: "",
  location: "",
  date: "",
  team: "",
  staging: "",
  equipment: "",
  safety: "",
  notes: "",
  linked_target_ids: [],
  attachments: [],
};

const emptyTimelineForm = {
  title: "",
  event_type: "Observation",
  priority: "Normal",
  date: "",
  location: "",
  county: "",
  description: "",
  linked_target_id: "",
  vehicle: "",
  attachments: [],
};

const emptyOfficerSafetyForm = {
  title: "",
  subjectName: "",
  category: "Officer Safety",
  riskLevel: "Critical",
  summary: "",
  regions: [],
  agencies: [],
  linkedTargetId: "",
  vehicle: "",
  location: "",
  expiresAt: "",
  attachments: [],
};

const MAP_LOCATIONS = [
  { id: "saguache", name: "Saguache", county: "Saguache", x: 44, y: 34 },
  { id: "villa-grove", name: "Villa Grove", county: "Saguache", x: 58, y: 22 },
  { id: "la-garita", name: "La Garita", county: "Saguache", x: 28, y: 48 },
  { id: "center", name: "Center", county: "Saguache", x: 52, y: 58 },
  { id: "crestone", name: "Crestone", county: "Saguache", x: 75, y: 43 },
  { id: "moffat", name: "Moffat", county: "Saguache", x: 68, y: 52 },
  { id: "south-fork", name: "South Fork", county: "Rio Grande", x: 18, y: 76 },
  { id: "del-norte", name: "Del Norte", county: "Rio Grande", x: 30, y: 70 },
  { id: "monte-vista", name: "Monte Vista", county: "Rio Grande", x: 40, y: 73 },
  { id: "alamosa", name: "Alamosa", county: "Alamosa", x: 61, y: 78 },
  { id: "mosca", name: "Mosca", county: "Alamosa", x: 67, y: 66 },
  { id: "hooper", name: "Hooper", county: "Alamosa", x: 71, y: 61 },
  { id: "manassa", name: "Manassa", county: "Conejos", x: 54, y: 92 },
  { id: "la-jara", name: "La Jara", county: "Conejos", x: 50, y: 89 },
  { id: "sanford", name: "Sanford", county: "Conejos", x: 58, y: 88 },
];



const COUNTY_ORDER = ["Saguache", "Rio Grande", "Alamosa", "Conejos", "Regional / Unknown"];

function getTargetCounties(target) {
  const towns = ensureArray(target?.associated_towns);
  const regions = ensureArray(target?.associated_regions);
  const counties = new Set();

  regions.forEach((region) => {
    if (DEFAULT_ALERT_REGIONS.includes(region)) counties.add(region);
  });

  towns.forEach((town) => {
    const match = MAP_LOCATIONS.find((place) => place.name === town);
    if (match?.county) counties.add(match.county);
  });

  const locationText = [target?.addresses, target?.summary, target?.notes]
    .flat()
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  MAP_LOCATIONS.forEach((place) => {
    if (locationText.includes(place.name.toLowerCase())) counties.add(place.county);
  });

  if (!counties.size) counties.add("Regional / Unknown");
  return Array.from(counties);
}

function getTargetTowns(target) {
  return ensureArray(target?.associated_towns);
}

function getTownsForCounty(county) {
  return MAP_LOCATIONS.filter((place) => place.county === county);
}

const EVENT_TYPES = ["Observation", "Traffic Stop", "BOLO", "Officer Safety", "Surveillance", "Operation", "Vehicle Sighting", "Contact", "Other"];



function getPermanentAdminProfile() {
  return {
    id: "admin_matt",
    username: "admin",
    display_name: "Matt Admin",
    role: "Admin",
    user_role: "Admin",
    agency_id: "SCSO",
    assigned_regions: ["SLV"],
    assignedRegions: ["SLV"],
    isApproved: true,
    is_approved: true,
    is_admin: true,
  };
}

function loadAdminProfile() {
  try {
    const raw = localStorage.getItem(ADMIN_PROFILE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") {
      return { ...getPermanentAdminProfile(), ...parsed, role: parsed.role || "Admin", user_role: parsed.user_role || parsed.role || "Admin" };
    }
  } catch {}
  return getPermanentAdminProfile();
}

function saveAdminProfile(profile) {
  try {
    localStorage.setItem(ADMIN_PROFILE_STORAGE_KEY, JSON.stringify(profile || getPermanentAdminProfile()));
  } catch {}
}

function getUserRole(user) {
  return String(user?.role || user?.user_role || "Admin").toLowerCase();
}

function getUserRoleLabel(user) {
  const role = getUserRole(user);
  if (role.includes("admin")) return "Admin";
  if (role.includes("investigation") || role.includes("detective") || role.includes("investigator")) return "Investigations";
  return "Patrol";
}

function isPatrolUser(user) {
  return getUserRoleLabel(user) === "Patrol";
}

function isInvestigationsUser(user) {
  return getUserRoleLabel(user) === "Investigations";
}

function isAdminUser(user) {
  return getUserRoleLabel(user) === "Admin" || user?.is_admin === true;
}

function isSupervisorUser(user) {
  return isAdminUser(user) || isInvestigationsUser(user);
}

function canCreateIntel(user) {
  return Boolean(user);
}

function canCreateOfficerSafety(user) {
  return isInvestigationsUser(user) || isAdminUser(user);
}

function canViewSubjectProfiles(user) {
  return isInvestigationsUser(user) || isAdminUser(user);
}

function canCreateTargetProfile(user) {
  return isInvestigationsUser(user) || isAdminUser(user);
}

function canCreateOperation(user) {
  return isInvestigationsUser(user) || isAdminUser(user);
}

function canDeleteOrDeactivate(user) {
  return isAdminUser(user);
}

function canAssignRoles(user) {
  return isAdminUser(user);
}

function canManageRegions(user) {
  return isAdminUser(user);
}

function canViewAuditTrail(user) {
  return isAdminUser(user);
}

function normalizeRegionName(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const normalized = raw.replace(/_/g, " ").toLowerCase();
  if (normalized === "slv" || normalized === "san luis valley") return "SLV";
  const known = DEFAULT_ALERT_REGIONS.find((region) => region.toLowerCase() === normalized || `${region.toLowerCase()} county` === normalized);
  return known || raw;
}

function expandAuthorizedRegions(regions) {
  const normalized = ensureArray(regions).map(normalizeRegionName).filter(Boolean);
  const expanded = new Set(normalized);
  normalized.forEach((region) => {
    if (region === "SLV") {
      DEFAULT_ALERT_REGIONS.forEach((subRegion) => expanded.add(subRegion));
    }
  });
  return Array.from(expanded);
}

function getAgencyDefaultRegions(user) {
  const agency = String(user?.agency_id || user?.agency || user?.agency_short_name || "").toUpperCase();
  if (agency.includes("SCSO") || agency.includes("SAGUACHE")) return ["Saguache"];
  if (agency.includes("RGSO") || agency.includes("RIO")) return ["Rio Grande"];
  if (agency.includes("ACSO") || agency.includes("ALAMOSA")) return ["Alamosa"];
  if (agency.includes("CCSO") || agency.includes("CONEJOS")) return ["Conejos"];
  return [];
}

function getUserAssignedRegions(user) {
  const direct = [
    ...ensureArray(user?.assigned_regions),
    ...ensureArray(user?.assignedRegions),
    ...ensureArray(user?.regions),
    ...ensureArray(user?.region),
    ...ensureArray(user?.assigned_area),
    ...ensureArray(user?.assignedArea),
    ...ensureArray(user?.area),
    ...ensureArray(user?.county),
  ]
    .map(normalizeRegionName)
    .filter(Boolean);

  const unique = Array.from(new Set(direct));
  return unique.length ? unique : getAgencyDefaultRegions(user);
}

const PATROL_VIEWABLE_POST_TYPES = [
  "Officer Safety Alert",
  "BOLO",
  "Attempt to Locate",
  "Information Release",
  "Information Bulletin",
  "Suspicious Activity",
];

function canPatrolViewPostType(postType) {
  return PATROL_VIEWABLE_POST_TYPES.includes(String(postType || ""));
}

function itemMatchesRegions(item, allowedRegions, allRegions = []) {
  const allowed = new Set(expandAuthorizedRegions(allowedRegions).map(normalizeRegionName).filter(Boolean));
  if (!allowed.size) return false;

  const itemRegions = new Set([
    ...ensureArray(item?.alert_regions),
    ...ensureArray(item?.regions),
    ...ensureArray(item?.associated_regions),
    ...getPostRegionNames(item || {}, ensureArray(allRegions)),
  ].map(normalizeRegionName).filter(Boolean));

  if (!itemRegions.size) {
    const inferred = normalizeRegionName(getLocationRegionFromText([item?.title, item?.summary, item?.body, item?.location, item?.county].join(" ")));
    if (inferred) itemRegions.add(inferred);
  }

  return Array.from(itemRegions).some((region) => allowed.has(region));
}

function userCanViewIntelItem(user, item, allRegions = []) {
  if (isAdminUser(user) || isInvestigationsUser(user)) return true;
  if (!isPatrolUser(user)) return false;
  if (!canPatrolViewPostType(item?.post_type || item?.type || item?.category)) return false;
  return itemMatchesRegions(item, getUserAssignedRegions(user), allRegions);
}

function userCanViewOfficerSafetyAlert(user, alert, allRegions = []) {
  if (isAdminUser(user) || isInvestigationsUser(user)) return true;
  if (!isPatrolUser(user)) return false;
  const alertRegions = getOfficerSafetyRegions(alert);
  return itemMatchesRegions({ ...(alert || {}), regions: alertRegions }, getUserAssignedRegions(user), allRegions);
}

function canViewNavItem(user, item) {
  if (!item) return false;
  if (item.key === "targets") return canViewSubjectProfiles(user);
  if (item.key === "operations") return isInvestigationsUser(user) || isAdminUser(user);
  if (item.key === "admin") return isAdminUser(user);
  if (item.key === "audit") return canViewAuditTrail(user);
  return true;
}


function PermissionNotice({ children }) {
  return (
    <Card>
      <div style={{ color: "#ffcf8a", fontWeight: 900, marginBottom: 6 }}>
        Permission Required
      </div>
      <div style={{ color: "#dce6ef", lineHeight: 1.45 }}>
        {children || "Your account does not have permission to perform this action."}
      </div>
    </Card>
  );
}


export default function App() {
  const [nav, setNav] = useState("dashboard");
  const [me, setMe] = useState(null);
  const [adminProfile, setAdminProfile] = useState(() => loadAdminProfile());
  const [intel, setIntel] = useState([]);
  const [regions, setRegions] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [operations, setOperations] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState(() => loadLocalTimeline());
  const [officerSafetyAlerts, setOfficerSafetyAlerts] = useState(() => loadLocalOfficerSafetyAlerts());
  const [auditLogs, setAuditLogs] = useState(() => loadLocalAudit());
  const [selectedTargetId, setSelectedTargetId] = useState("");
  const [selectedChatUser, setSelectedChatUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDisclosure, setShowDisclosure] = useState(() => {
    try {
      return localStorage.getItem(DISCLOSURE_STORAGE_KEY) !== "accepted";
    } catch {
      return true;
    }
  });

  const [intelForm, setIntelForm] = useState({
    title: "",
    post_type: "BOLO",
    priority: "Normal",
    summary: "",
    body: "",
    expires_at: "",
    targets: [],
    linked_target_ids: [],
    attachments: [],
  });

  const [targetForm, setTargetForm] = useState(emptyTargetForm);
  const [operationForm, setOperationForm] = useState(emptyOperationForm);
  const [timelineForm, setTimelineForm] = useState(emptyTimelineForm);
  const [officerSafetyForm, setOfficerSafetyForm] = useState(emptyOfficerSafetyForm);
  const [officerSafetyPanelMode, setOfficerSafetyPanelMode] = useState("list");
  const [timelineSearch, setTimelineSearch] = useState("");
  const [timelineTargetFilter, setTimelineTargetFilter] = useState("All");
  const [mapCountyFilter, setMapCountyFilter] = useState("All");
  const [alertSubscriptions, setAlertSubscriptions] = useState(() => loadAlertSubscriptions());
  const [seenDashboardAlertIds, setSeenDashboardAlertIds] = useState(() => loadSeenDashboardAlerts());
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [readNotifications, setReadNotifications] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("spear_read_notifications_v1") || "[]");
    } catch {
      return [];
    }
  });

  const [targetSearch, setTargetSearch] = useState("");
  const [isTargetFormOpen, setIsTargetFormOpen] = useState(false);
  const [expandedProfileRegions, setExpandedProfileRegions] = useState({});
  const [editingTargetId, setEditingTargetId] = useState("");
  const [intelSearch, setIntelSearch] = useState("");
  const [intelPriority, setIntelPriority] = useState("All");
  const [intelType, setIntelType] = useState("All");
  const [messageBody, setMessageBody] = useState("");
  const [newRegion, setNewRegion] = useState({ id: "", name: "", description: "" });
  const [adminUserDraft, setAdminUserDraft] = useState({
    display_name: "",
    email: "",
    role: "Patrol",
    agency_id: "SCSO",
    assigned_regions: ["Saguache"],
  });

  const effectiveMe = useMemo(() => ({ ...loadAdminProfile(), ...(me || {}), ...(adminProfile || {}) }), [me, adminProfile]);
  const assignedAccessRegions = useMemo(() => getUserAssignedRegions(effectiveMe), [effectiveMe]);
  const accessRoleLabel = getUserRoleLabel(effectiveMe);

  const permissions = {
    roleLabel: accessRoleLabel,
    assignedRegions: assignedAccessRegions,
    isPatrol: isPatrolUser(effectiveMe),
    isInvestigations: isInvestigationsUser(effectiveMe),
    isAdmin: isAdminUser(effectiveMe),
    isSupervisor: isSupervisorUser(effectiveMe),
    canCreateIntel: canCreateIntel(effectiveMe),
    canCreateOfficerSafety: canCreateOfficerSafety(effectiveMe),
    canViewSubjectProfiles: canViewSubjectProfiles(effectiveMe),
    canCreateTargetProfile: canCreateTargetProfile(effectiveMe),
    canCreateOperation: canCreateOperation(effectiveMe),
    canDeleteOrDeactivate: canDeleteOrDeactivate(effectiveMe),
    canAssignRoles: canAssignRoles(effectiveMe),
    canManageRegions: canManageRegions(effectiveMe),
    canViewAuditTrail: canViewAuditTrail(effectiveMe),
  };



  function logAction(action, details = "") {
    const entry = {
      id: makeId("audit"),
      action,
      details,
      user: effectiveMe?.display_name || effectiveMe?.username || "Unknown user",
      agency: effectiveMe?.agency_id || "Unknown agency",
      created_at: new Date().toISOString(),
    };
    setAuditLogs((prev) => {
      const next = [entry, ...prev].slice(0, 250);
      saveLocalAudit(next);
      return next;
    });
  }

  function clearAuditLog() {
    if (!permissions.isAdmin) {
      setError("Only administrators can clear the audit log.");
      return;
    }
    if (!confirm("Clear local audit log for this browser?")) return;
    saveLocalAudit([]);
    setAuditLogs([]);
    setNotice("Local audit log cleared.");
  }

  async function approveUserLocal(userId) {
    setError("");
    setNotice("");
    try {
      await api("/api/admin-users", {
        method: "POST",
        body: JSON.stringify({ userId, action: "approve" }),
      });
      await loadUsers();
      logAction("User approved", `User ID: ${userId}`);
      setNotice("User approved.");
    } catch (e) {
      setUsers((prev) =>
        ensureArray(prev).map((u) =>
          u.id === userId
            ? { ...u, isApproved: true, is_approved: true, status: "Approved" }
            : u
        )
      );
      setNotice("User approved locally. Backend approval route returned an error.");
    }
  }

  async function suspendUserLocal(userId) {
    setError("");
    setNotice("");
    try {
      await api("/api/admin-users", {
        method: "POST",
        body: JSON.stringify({ userId, action: "suspend" }),
      });
      await loadUsers();
      logAction("User suspended", `User ID: ${userId}`);
      setNotice("User suspended.");
    } catch (e) {
      setUsers((prev) =>
        ensureArray(prev).map((u) =>
          u.id === userId
            ? { ...u, isDisabled: true, is_disabled: true, status: "Suspended" }
            : u
        )
      );
      setNotice("User suspended locally. Backend suspend route returned an error.");
    }
  }


  function updateUserAccessLocal(userId, role) {
    setUsers((prev) =>
      ensureArray(prev).map((user) =>
        user?.id === userId
          ? { ...user, role, user_role: role }
          : user
      )
    );
    logAction("User role updated", `User ID: ${userId} • Role: ${role}`);
    setNotice("User role updated locally. Wire this to /api/users for production persistence.");
  }

  function toggleUserAssignedRegionLocal(userId, regionName) {
    setUsers((prev) =>
      ensureArray(prev).map((user) => {
        if (user?.id !== userId) return user;
        const current = getUserAssignedRegions(user);
        const exists = current.includes(regionName);
        const next = exists ? current.filter((x) => x !== regionName) : [...current, regionName];
        return { ...user, assigned_regions: next, assignedRegions: next };
      })
    );
    logAction("User assigned region updated", `User ID: ${userId} • Region: ${regionName}`);
    setNotice("User region assignment updated locally. Wire this to /api/users for production persistence.");
  }


  function updateAdminProfileField(field, value) {
    const next = { ...(adminProfile || loadAdminProfile()), [field]: value };
    setAdminProfile(next);
    saveAdminProfile(next);
    setMe((prev) => ({ ...(prev || {}), ...next }));
  }

  function updateAdminAssignedRegion(regionName) {
    const current = getUserAssignedRegions(adminProfile);
    const exists = current.includes(regionName);
    const nextRegions = exists ? current.filter((x) => x !== regionName) : [...current, regionName];
    const next = { ...(adminProfile || loadAdminProfile()), assigned_regions: nextRegions, assignedRegions: nextRegions };
    setAdminProfile(next);
    saveAdminProfile(next);
    setMe((prev) => ({ ...(prev || {}), ...next }));
    setNotice(`Admin assigned regions updated: ${nextRegions.join(", ") || "None"}.`);
  }

  function createAdminManagedUser(e) {
    e.preventDefault();
    const displayName = String(adminUserDraft.display_name || "").trim();
    if (!displayName) {
      setError("Display name is required.");
      return;
    }

    const user = {
      id: makeId("user"),
      display_name: displayName,
      displayName,
      email: adminUserDraft.email || "",
      username: adminUserDraft.email || displayName.toLowerCase().replace(/\s+/g, "."),
      role: adminUserDraft.role || "Patrol",
      user_role: adminUserDraft.role || "Patrol",
      agency_id: adminUserDraft.agency_id || "SCSO",
      assigned_regions: ensureArray(adminUserDraft.assigned_regions),
      assignedRegions: ensureArray(adminUserDraft.assigned_regions),
      isApproved: true,
      is_approved: true,
      status: "Approved",
      created_at: new Date().toISOString(),
    };

    setUsers((prev) => [user, ...ensureArray(prev)]);
    setAdminUserDraft({
      display_name: "",
      email: "",
      role: "Patrol",
      agency_id: "SCSO",
      assigned_regions: ["Saguache"],
    });
    logAction("Admin created user", `${user.display_name} • ${user.role}`);
    setNotice("User created locally. Backend persistence can be connected next.");
  }

  function updateManagedUserRole(userId, role) {
    setUsers((prev) => ensureArray(prev).map((user) => user?.id === userId ? { ...user, role, user_role: role, updated_at: new Date().toISOString() } : user));
    logAction("Admin updated user role", `${userId} • ${role}`);
  }

  function toggleManagedUserRegion(userId, regionName) {
    setUsers((prev) => ensureArray(prev).map((user) => {
      if (user?.id !== userId) return user;
      const current = getUserAssignedRegions(user);
      const exists = current.includes(regionName);
      const nextRegions = exists ? current.filter((x) => x !== regionName) : [...current, regionName];
      return { ...user, assigned_regions: nextRegions, assignedRegions: nextRegions, updated_at: new Date().toISOString() };
    }));
    logAction("Admin updated user region access", `${userId} • ${regionName}`);
  }

  async function loadMe() {
    try {
      const data = await api("/api/me");
      setMe({ ...loadAdminProfile(), ...(data.user || data.me || {}) });
    } catch {
      setMe(loadAdminProfile());
    }
  }

  async function loadIntel() {
    const localPosts = loadLocalIntel();
    if (localPosts.length) setIntel(localPosts);
    try {
      const data = await api("/api/intel");
      const serverPosts = ensureArray(data.intel);
      if (serverPosts.length) {
        setIntel(serverPosts);
        saveLocalIntel(serverPosts);
      } else if (!localPosts.length) {
        setIntel([]);
      }
    } catch {
      setIntel(localPosts);
    }
  }

  async function loadRegions() {
    const localRegions = loadLocalRegions();
    setRegions(localRegions);
    try {
      const data = await api("/api/regions");
      const serverRegions = ensureArray(data.regions);
      const byId = new Map([...DEFAULT_REGIONS, ...serverRegions].map((r) => [String(r.id || r.name).toUpperCase(), r]));
      const merged = Array.from(byId.values());
      setRegions(merged);
      saveLocalRegions(merged);
    } catch {
      setRegions(localRegions);
    }
  }

  async function loadAgencies() {
    try {
      const data = await api("/api/agencies");
      const serverAgencies = ensureArray(data.agencies);
      setAgencies(serverAgencies.length ? serverAgencies : DEFAULT_AGENCIES);
    } catch {
      setAgencies(DEFAULT_AGENCIES);
    }
  }

  async function loadUsers() {
    try {
      const data = await api("/api/users");
      setUsers(ensureArray(data.users));
    } catch {
      setUsers([]);
    }
  }

  async function loadTargets() {
    const localTargets = loadLocalTargets();
    setTargets(localTargets);
    if (localTargets[0]?.id) setSelectedTargetId((prev) => prev || localTargets[0].id);

    try {
      const data = await api("/api/targets");
      const serverTargets = data.targets || data.profiles || [];
      if (Array.isArray(serverTargets) && serverTargets.length) {
        const normalizedServerTargets = serverTargets.map(normalizeTargetProfile);
        setTargets(normalizedServerTargets);
        saveLocalTargets(normalizedServerTargets);
        if (normalizedServerTargets[0]?.id) setSelectedTargetId((prev) => prev || normalizedServerTargets[0].id);
      }
    } catch {
      // Backend endpoint is optional for this upgrade. Local browser storage keeps the UI usable now.
    }
  }

  async function loadOps() {
    const localOps = loadLocalOps();
    setOperations(localOps);

    try {
      const data = await api("/api/operations");
      const serverOps = data.operations || data.ops || [];
      if (Array.isArray(serverOps) && serverOps.length) {
        setOperations(serverOps);
        saveLocalOps(serverOps);
      }
    } catch {
      // Backend endpoint is optional for this upgrade. Local browser storage keeps the board usable now.
    }
  }

  async function loadAll() {
    setLoading(true);
    setError("");
    try {
      await loadMe();
      await Promise.all([loadIntel(), loadRegions(), loadAgencies(), loadUsers(), loadTargets(), loadOps()]);
    } catch (e) {
      // Keep the MVP usable even when optional backend routes are not built yet.
      await loadTargets();
      await loadOps();
      setAgencies((prev) => ensureArray(prev).length ? prev : DEFAULT_AGENCIES);
      setRegions((prev) => ensureArray(prev).length ? prev : loadLocalRegions());
    } finally {
      setLoading(false);
    }
  }

  async function openConversation(user) {
    setSelectedChatUser(user);
    setMessageBody("");
    try {
      const data = await api(`/api/messages?user_id=${encodeURIComponent(user.id)}`);
      setMessages(ensureArray(data.messages));
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const currentNav = NAV.find((item) => item.key === nav);
    if (currentNav && !canViewNavItem(effectiveMe, currentNav)) {
      setNav("dashboard");
    }
  }, [nav, effectiveMe]);


  const alertRegionOptions = useMemo(() => {
    const names = new Set(DEFAULT_ALERT_REGIONS);
    ensureArray(regions).forEach((region) => {
      if (region?.name) names.add(region.name);
      else if (region?.id) names.add(region?.id);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [regions]);

  const subscribedAlerts = useMemo(() => {
    return ensureArray(intel)
      .map((item) => ({ ...(item || {}), alert_regions: getPostRegionNames(item || {}, ensureArray(regions)) }))
      .filter((item) => userCanViewIntelItem(effectiveMe, item, ensureArray(regions)))
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [intel, regions, effectiveMe]);

  const visibleIntel = subscribedAlerts;

  const subscribedOfficerSafetyAlerts = useMemo(() => {
    return ensureArray(officerSafetyAlerts)
      .filter((alert) => alert?.isActive !== false)
      .filter((alert) => userCanViewOfficerSafetyAlert(effectiveMe, alert, ensureArray(regions)))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [officerSafetyAlerts, regions, effectiveMe]);



  const dashboardAlertItems = useMemo(() => {
    const allowedRegionSet = new Set(permissions.isPatrol ? ensureArray(permissions.assignedRegions) : DEFAULT_ALERT_REGIONS);
    const isSubscribedRegion = (regionName) => {
      if (!regionName) return !permissions.isPatrol;
      if (!permissions.isPatrol) return true;
      return allowedRegionSet.has(regionName);
    };

    const regionFromText = (parts) => {
      const text = ensureArray(parts).join(" ");
      const direct = getLocationRegionFromText(text);
      if (direct) return direct;
      for (const regionName of allowedRegionSet) {
        if (textMatchesRegion(text, regionName)) return regionName;
      }
      return "";
    };

    const items = [];

    ensureArray(subscribedAlerts).forEach((item) => {
      const idBase = item?.id || `${item?.title || "info"}-${item?.created_at || ""}`;
      const regionsForItem = ensureArray(item?.alert_regions);
      const regionLabel = regionsForItem.length ? regionsForItem.join(", ") : "Subscribed regions";

      if (item?.priority === "Critical") {
        items.push({
          id: `critical-${idBase}`,
          category: "Critical Alerts",
          title: item?.title || "Critical Alert",
          detail: item?.summary || item?.body || "Critical regional information.",
          date: item?.created_at || item?.updated_at,
          region: regionLabel,
          action: () => setNav("intel"),
        });
      }

      items.push({
        id: `info-${idBase}`,
        category: "Information Postings",
        title: item?.title || "Information Posting",
        detail: item?.summary || item?.body || "New information posting.",
        date: item?.created_at || item?.updated_at,
        region: regionLabel,
        action: () => setNav("intel"),
      });
    });

    ensureArray(targets).forEach((target) => {
      const safeTarget = normalizeTargetProfile(target || {});
      const counties = getTargetCounties(safeTarget);
      if (!counties.some(isSubscribedRegion)) return;

      items.push({
        id: `target-${safeTarget.id}-${safeTarget.updated_at || safeTarget.created_at || ""}`,
        category: "Target Profiles",
        title: safeTarget.name || "Subject Profile",
        detail: safeTarget.summary || safeTarget.status || "Subject profile update.",
        date: safeTarget.updated_at || safeTarget.created_at,
        region: counties.join(", "),
        action: () => {
          setSelectedTargetId(safeTarget.id);
          setNav("targets");
        },
      });
    });

    ensureArray(operations).forEach((op) => {
      if (!(op?.status === "Active" || op?.status === "Planning")) return;
      const regionName = regionFromText([op?.location, op?.title, op?.objective]);
      if (!isSubscribedRegion(regionName)) return;

      items.push({
        id: `op-${op?.id || op?.title}-${op?.updated_at || op?.created_at || ""}`,
        category: "Active Ops",
        title: op?.title || "Active Operation",
        detail: op?.objective || op?.location || "Active operation update.",
        date: op?.updated_at || op?.created_at || op?.date,
        region: regionName || "Subscribed regions",
        action: () => setNav("operations"),
      });
    });

    ensureArray(timelineEvents).forEach((event) => {
      const regionName = event?.county || regionFromText([event?.location, event?.title, event?.description]);
      if (!isSubscribedRegion(regionName)) return;

      items.push({
        id: `timeline-${event?.id || event?.title}-${event?.updated_at || event?.created_at || event?.date || ""}`,
        category: "Timeline Events",
        title: event?.title || "Timeline Event",
        detail: event?.description || event?.location || "Timeline activity update.",
        date: event?.updated_at || event?.created_at || event?.date,
        region: regionName || "Subscribed regions",
        action: () => setNav("timeline"),
      });
    });

    return items
      .filter((item) => !ensureArray(seenDashboardAlertIds).includes(item.id))
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [subscribedAlerts, subscribedOfficerSafetyAlerts, targets, operations, timelineEvents, permissions.isPatrol, permissions.assignedRegions, seenDashboardAlertIds]);

  const dashboardAlertCounts = useMemo(() => {
    const counts = {
      "Officer Safety": 0,
      "Critical Alerts": 0,
      "Target Profiles": 0,
      "Information Postings": 0,
      "Active Ops": 0,
      "Timeline Events": 0,
    };

    dashboardAlertItems.forEach((item) => {
      if (counts[item.category] !== undefined) counts[item.category] += 1;
    });

    return counts;
  }, [dashboardAlertItems]);



  const allNotifications = useMemo(() => {
    const notifications = [];

    ensureArray(subscribedOfficerSafetyAlerts).forEach((alert) => {
      notifications.push({
        id: `officer_safety_${alert?.id || alert?.createdAt || alert?.title}`,
        category: "Officer Safety",
        title: alert?.title || "Officer Safety Alert",
        subtitle: alert?.summary || "",
        source: "Officer Safety",
        createdAt: alert?.createdAt || new Date().toISOString(),
      });
    });

    ensureArray(subscribedAlerts).forEach((item) => {
      notifications.push({
        id: `intel_${item?.id || item?.created_at || item?.title}`,
        category: item?.priority === "Critical" ? "Critical Alerts" : "Information Postings",
        title: item?.title || "Information Posting",
        subtitle: item?.summary || "",
        source: "Regional Feed",
        createdAt: item?.created_at || new Date().toISOString(),
      });
    });

    ensureArray(targets).forEach((target) => {
      notifications.push({
        id: `target_${target?.id || target?.name}`,
        category: "Target Profiles",
        title: target?.name || "Subject Profile",
        subtitle: target?.summary || "",
        source: "Subject Profiles",
        createdAt: target?.updated_at || target?.created_at || new Date().toISOString(),
      });
    });

    ensureArray(operations).forEach((op) => {
      notifications.push({
        id: `operation_${op?.id || op?.title}`,
        category: "Active Ops",
        title: op?.title || "Operation",
        subtitle: op?.objective || "",
        source: "Operations Board",
        createdAt: op?.updated_at || op?.created_at || new Date().toISOString(),
      });
    });

    ensureArray(timelineEvents).forEach((event) => {
      notifications.push({
        id: `timeline_${event?.id || event?.title}`,
        category: "Timeline Events",
        title: event?.title || "Timeline Event",
        subtitle: event?.description || "",
        source: "Activity Timeline",
        createdAt: event?.updated_at || event?.created_at || new Date().toISOString(),
      });
    });

    return notifications.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [subscribedOfficerSafetyAlerts, subscribedAlerts, targets, operations, timelineEvents]);

  const unreadNotifications = useMemo(() => {
    return ensureArray(allNotifications).filter((note) => !ensureArray(readNotifications).includes(note.id));
  }, [allNotifications, readNotifications]);

  function markNotificationRead(id) {
    const next = Array.from(new Set([...ensureArray(readNotifications), id]));
    setReadNotifications(next);
    try {
      localStorage.setItem("spear_read_notifications_v1", JSON.stringify(next));
    } catch {}
  }

  function markAllNotificationsRead() {
    const next = Array.from(new Set(ensureArray(allNotifications).map((x) => x.id)));
    setReadNotifications(next);
    try {
      localStorage.setItem("spear_read_notifications_v1", JSON.stringify(next));
    } catch {}
  }

  const dashboardStats = useMemo(() => {
    const critical = ensureArray(intel).filter((x) => x?.priority === "Critical").length;
    const high = ensureArray(intel).filter((x) => x?.priority === "High").length;
    const officerSafetyTargets = ensureArray(targets).filter((x) => safeIncludes(x?.flags, "Officer Safety Hazard")).length;
    return {
      totalIntel: intel.length,
      critical,
      high,
      regions: ensureArray(regions).length,
      users: ensureArray(users).length,
      targets: ensureArray(targets).length,
      officerSafetyTargets,
      activeOps: ensureArray(operations).filter((op) => op?.status === "Active" || op?.status === "Planning").length,
      pendingUsers: ensureArray(users).filter((u) => u?.isApproved === false || u?.is_approved === false || u?.status === "Pending").length,
      auditEvents: ensureArray(auditLogs).length,
      timelineEvents: ensureArray(timelineEvents).length,
      mapPins: ensureArray(timelineEvents).length + ensureArray(operations).length + ensureArray(targets).length,
      subscribedAlerts: ensureArray(subscribedAlerts).length,
    };
  }, [intel, regions, users, targets, operations, auditLogs, timelineEvents, subscribedAlerts]);

  const selectedTarget = useMemo(
    () => ensureArray(targets).find((x) => x?.id === selectedTargetId) || ensureArray(targets)[0] || null,
    [targets, selectedTargetId]
  );


  const selectedTargetKey = selectedTarget?.id || selectedTargetId || "";

  const relatedTargetIntel = useMemo(() => {
    if (!selectedTargetKey) return [];
    return ensureArray(intel).filter((item) =>
      ensureArray(item?.linked_target_ids).includes(selectedTargetKey)
    );
  }, [selectedTargetKey, intel]);

  const relatedTargetOperations = useMemo(() => {
    if (!selectedTargetKey) return [];
    return ensureArray(operations).filter((item) =>
      ensureArray(item?.linked_target_ids).includes(selectedTargetKey)
    );
  }, [selectedTargetKey, operations]);

  const relatedTargetTimeline = useMemo(() => {
    if (!selectedTargetKey) return [];
    return ensureArray(timelineEvents).filter((item) =>
      ensureArray(item?.linked_target_ids).includes(selectedTargetKey) ||
      item?.linkedTargetId === selectedTargetKey ||
      item?.linked_target_id === selectedTargetKey
    );
  }, [selectedTargetKey, timelineEvents]);

  const relatedTargetOfficerSafety = useMemo(() => {
    if (!selectedTargetKey) return [];
    return ensureArray(officerSafetyAlerts).filter((item) =>
      item?.linkedTargetId === selectedTargetKey ||
      item?.linked_target_id === selectedTargetKey ||
      ensureArray(item?.linked_target_ids).includes(selectedTargetKey)
    );
  }, [selectedTargetKey, officerSafetyAlerts]);

  const safeTargetForm = {
    ...emptyTargetForm,
    ...(targetForm || {}),
    associated_regions: ensureArray(targetForm?.associated_regions),
    associated_towns: ensureArray(targetForm?.associated_towns),
    flags: ensureArray(targetForm?.flags),
    attachments: ensureArray(targetForm?.attachments),
  };

  const safeOperationForm = {
    ...emptyOperationForm,
    ...(operationForm || {}),
    title: textValue(operationForm?.title),
    status: textValue(operationForm?.status) || "Planning",
    priority: textValue(operationForm?.priority) || "High",
    objective: textValue(operationForm?.objective),
    location: textValue(operationForm?.location),
    date: textValue(operationForm?.date),
    team: textValue(operationForm?.team),
    staging: textValue(operationForm?.staging),
    equipment: textValue(operationForm?.equipment),
    safety: textValue(operationForm?.safety),
    notes: textValue(operationForm?.notes),
    linked_target_ids: ensureArray(operationForm?.linked_target_ids),
    attachments: ensureArray(operationForm?.attachments),
  };

  const safeIntelForm = {
    title: textValue(intelForm?.title),
    post_type: textValue(intelForm?.post_type) || "BOLO",
    priority: textValue(intelForm?.priority) || "Normal",
    summary: textValue(intelForm?.summary),
    body: textValue(intelForm?.body),
    expires_at: textValue(intelForm?.expires_at),
    targets: ensureArray(intelForm?.targets),
    linked_target_ids: ensureArray(intelForm?.linked_target_ids),
    attachments: ensureArray(intelForm?.attachments),
  };

  const safeTargets = ensureArray(targets).map((target) => normalizeTargetProfile(target || {}));
  const safeRegions = ensureArray(regions).length ? ensureArray(regions) : DEFAULT_REGIONS;
  const safeAgencies = ensureArray(agencies).length ? ensureArray(agencies) : DEFAULT_AGENCIES;
  const safeUsers = ensureArray(users);
  const safeOperations = ensureArray(operations);
  const safeTimelineEvents = ensureArray(timelineEvents);
  const safeAuditLogs = ensureArray(auditLogs);


  function toggleProfileRegionPanel(regionName) {
    setExpandedProfileRegions((prev) => ({ ...(prev || {}), [regionName]: !prev?.[regionName] }));
  }

  function toggleProfileRegionSelection(regionName) {
    setTargetForm((prev) => {
      const currentRegions = ensureArray(prev?.associated_regions);
      const currentTowns = ensureArray(prev?.associated_towns);
      const countyTowns = getTownsForCounty(regionName).map((place) => place.name);
      const exists = currentRegions.includes(regionName);
      return {
        ...(prev || emptyTargetForm),
        associated_regions: exists ? currentRegions.filter((x) => x !== regionName) : [...currentRegions, regionName],
        associated_towns: exists
          ? currentTowns.filter((town) => !countyTowns.includes(town))
          : Array.from(new Set([...currentTowns, ...countyTowns])),
      };
    });
  }

  function toggleProfileTownSelection(place) {
    setTargetForm((prev) => {
      const currentTowns = ensureArray(prev?.associated_towns);
      const currentRegions = ensureArray(prev?.associated_regions);
      const exists = currentTowns.includes(place.name);
      const nextTowns = exists ? currentTowns.filter((x) => x !== place.name) : [...currentTowns, place.name];
      const countyTowns = getTownsForCounty(place.county).map((p) => p.name);
      const hasAnyCountyTown = nextTowns.some((town) => countyTowns.includes(town));
      const nextRegions = hasAnyCountyTown
        ? Array.from(new Set([...currentRegions, place.county]))
        : currentRegions.filter((region) => region !== place.county);
      return {
        ...(prev || emptyTargetForm),
        associated_towns: nextTowns,
        associated_regions: nextRegions,
      };
    });
  }


  function toggleAlertSubscription(regionName) {
    if (permissions.isPatrol) {
      setError("Patrol region access is assigned by an Admin and cannot be self-selected.");
      return;
    }
    setAlertSubscriptions((prev) => {
      const current = ensureArray(prev);
      const exists = current.includes(regionName);
      const next = exists ? current.filter((x) => x !== regionName) : [...current, regionName];
      saveAlertSubscriptions(next);
      logAction(exists ? "Alert region unsubscribed" : "Alert region subscribed", regionName);
      return next;
    });
  }

  const filteredTargets = useMemo(() => {
    const q = textValue(targetSearch).trim().toLowerCase();
    if (!q) return ensureArray(targets);
    return ensureArray(targets).filter((target) => {
      const haystack = [
        target.name,
        ...ensureArray(target.aliases),
        ...ensureArray(target.vehicles),
        ...getTargetTowns(target),
        ...getTargetCounties(target),
        ...ensureArray(target.addresses),
        ...ensureArray(target.associates),
        ...ensureArray(target.flags),
        target.summary,
        target.notes,
      ]
        .join(" ")
        .toLowerCase();
      return String(haystack || "").includes(q);
    });
  }, [targets, targetSearch]);

  const filteredTimelineEvents = useMemo(() => {
    const q = textValue(timelineSearch).trim().toLowerCase();
    return ensureArray(timelineEvents).filter((event) => {
      const matchesTarget = timelineTargetFilter === "All" || event?.linked_target_id === timelineTargetFilter;
      const haystack = [
        event.title,
        event.event_type,
        event.priority,
        event.location,
        event.county,
        event.description,
        event.vehicle,
        ensureArray(targets).find((t) => t?.id === event?.linked_target_id)?.name,
      ].join(" ").toLowerCase();
      return matchesTarget && (!q || String(haystack || "").includes(q));
    });
  }, [timelineEvents, timelineSearch, timelineTargetFilter, targets]);

  const mapItems = useMemo(() => {
    const items = [];

    ensureArray(targets).forEach((target, idx) => {
      const safeTarget = normalizeTargetProfile(target || {});
      const text = [safeTarget.name, ...getTargetTowns(safeTarget), ...ensureArray(safeTarget.addresses)].join(" ").toLowerCase();
      const loc = MAP_LOCATIONS.find((place) => text.includes(place.name.toLowerCase())) || MAP_LOCATIONS[idx % MAP_LOCATIONS.length];
      if (!loc) return;
      items.push({
        id: `target-${safeTarget.id}`,
        type: "Subject",
        label: safeTarget.name,
        priority: safeTarget.threat_level || "Normal",
        county: loc.county,
        x: loc.x + ((idx % 3) - 1) * 2,
        y: loc.y + ((idx % 2) - 0.5) * 2,
        detail: safeTarget.summary || safeTarget.status || "Subject profile",
      });
    });

    ensureArray(operations).forEach((op, idx) => {
      const text = [op?.title, op?.location].filter(Boolean).join(" ").toLowerCase();
      const loc = MAP_LOCATIONS.find((place) => text.includes(place.name.toLowerCase())) || MAP_LOCATIONS[(idx + 2) % MAP_LOCATIONS.length];
      if (!loc) return;
      items.push({
        id: `op-${op?.id || idx}`,
        type: "Operation",
        label: op?.title || "Untitled Operation",
        priority: op?.priority || "High",
        county: loc.county,
        x: loc.x + ((idx % 4) - 1.5) * 2,
        y: loc.y + ((idx % 3) - 1) * 2,
        detail: op?.objective || op?.location || "Operation",
      });
    });

    ensureArray(timelineEvents).forEach((event, idx) => {
      const text = [event?.title, event?.location, event?.county].filter(Boolean).join(" ").toLowerCase();
      const loc = MAP_LOCATIONS.find((place) => text.includes(place.name.toLowerCase()) || text.includes(place.county.toLowerCase())) || MAP_LOCATIONS[(idx + 4) % MAP_LOCATIONS.length];
      if (!loc) return;
      items.push({
        id: `event-${event?.id || idx}`,
        type: event?.event_type || "Event",
        label: event?.title || "Timeline Event",
        priority: event?.priority || "Normal",
        county: event?.county || loc.county,
        x: loc.x + ((idx % 5) - 2) * 1.8,
        y: loc.y + ((idx % 4) - 1.5) * 1.8,
        detail: event?.description || event?.location || "Timeline event",
      });
    });

    return mapCountyFilter === "All" ? items : items.filter((item) => item.county === mapCountyFilter);
  }, [targets, operations, timelineEvents, mapCountyFilter]);

  const filteredIntel = useMemo(() => {
    const q = textValue(intelSearch).trim().toLowerCase();
    return ensureArray(visibleIntel).filter((item) => {
      const matchesPriority = intelPriority === "All" || item?.priority === intelPriority;
      const matchesType = intelType === "All" || item?.post_type === intelType;
      const haystack = [item?.title, item?.post_type, item?.priority, item?.summary, item?.body, item?.created_by_name, item?.created_by_agency]
        .join(" ")
        .toLowerCase();
      const matchesSearch = !q || haystack.includes(q);
      return matchesPriority && matchesType && matchesSearch;
    });
  }, [visibleIntel, intelSearch, intelPriority, intelType]);

  function toggleOperationTarget(targetId) {
    setOperationForm((prev) => {
      const currentLinked = Array.isArray(prev?.linked_target_ids) ? prev.linked_target_ids : [];
      const exists = currentLinked.includes(targetId);
      return {
        ...emptyOperationForm,
        ...(prev || {}),
        linked_target_ids: exists
          ? currentLinked.filter((id) => id !== targetId)
          : [...currentLinked, targetId],
      };
    });
  }

  function toggleTarget(target_type, target_id) {
    setIntelForm((prev) => {
      const currentTargets = ensureArray(prev?.targets);
      const exists = currentTargets.some(
        (t) => t.target_type === target_type && t.target_id === target_id
      );

      const nextTargets = exists
        ? currentTargets.filter(
            (t) => !(t.target_type === target_type && t.target_id === target_id)
          )
        : [...currentTargets, { target_type, target_id }];

      return { ...(prev || {}), targets: nextTargets };
    });
  }

  function toggleIntelLinkedTarget(targetId) {
    setIntelForm((prev) => {
      const currentLinked = Array.isArray(prev?.linked_target_ids) ? prev.linked_target_ids : [];
      const exists = currentLinked.includes(targetId);
      return {
        title: "",
        post_type: "BOLO",
        priority: "Normal",
        summary: "",
        body: "",
        expires_at: "",
        targets: [],
        ...(prev || {}),
        linked_target_ids: exists
          ? currentLinked.filter((id) => id !== targetId)
          : [...currentLinked, targetId],
      };
    });
  }

  function toggleTargetFlag(flag) {
    setTargetForm((prev) => {
      const flags = ensureArray(prev.flags);
      const exists = safeIncludes(flags, flag);
      return {
        ...prev,
        flags: exists ? flags.filter((x) => x !== flag) : [...flags, flag],
      };
    });
  }

  async function submitIntel(e) {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!permissions.canCreateIntel) {
      setError("You must be logged in to submit information.");
      return;
    }


    if (!safeIntelForm.title.trim()) {
      setError("Information title is required.");
      return;
    }
    if (!safeIntelForm.summary.trim()) {
      setError("Information summary is required.");
      return;
    }

    const linkedProfiles = ensureArray(intelForm?.linked_target_ids)
      .map((id) => ensureArray(targets).find((x) => x?.id === id)?.name)
      .filter(Boolean);

    const payload = {
      id: makeId("info"),
      title: safeIntelForm.title,
      post_type: safeIntelForm.post_type,
      priority: safeIntelForm.priority,
      summary: safeIntelForm.summary,
      body: linkedProfiles.length
        ? `${safeIntelForm.body}

Linked Subject Profiles: ${linkedProfiles.join(", ")}`
        : safeIntelForm.body,
      expires_at: safeIntelForm.expires_at,
      targets: safeIntelForm.targets,
      linked_target_ids: safeIntelForm.linked_target_ids,
      attachments: safeIntelForm.attachments,
      created_by_name: effectiveMe?.display_name || effectiveMe?.username || "Current User",
      created_by_agency: effectiveMe?.agency_id || "Local",
      created_at: new Date().toISOString(),
    };

    let saved = payload;
    try {
      const data = await api("/api/intel", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      saved = data.intel || data.post || payload;
    } catch {
      // Backend endpoint is optional during MVP. Save locally instead of showing Request failed.
    }

    const nextIntel = [saved, ...ensureArray(intel)];
    setIntel(nextIntel);
    saveLocalIntel(nextIntel);

    setIntelForm({
      title: "",
      post_type: "BOLO",
      priority: "Normal",
      summary: "",
      body: "",
      expires_at: "",
      targets: [],
      linked_target_ids: [],
      attachments: [],
    });

    logAction("Information submitted", payload.title || "Untitled information post");
    setNotice("Information saved. Backend sync can be added later.");
    setNav("intel");
  }

  function startNewTargetProfile() {
    setEditingTargetId("");
    setTargetForm({ ...emptyTargetForm, associated_regions: [], associated_towns: [], flags: [] });
    setIsTargetFormOpen(true);
    setSelectedTargetId("");
    setNotice("");
    setError("");
  }

  function startEditTargetProfile(target) {
    if (!target) return;
    setEditingTargetId(target.id);
    setSelectedTargetId(target.id);
    setTargetForm({
      name: target.name || "",
      aliases: Array.isArray(target.aliases) ? target.aliases.join("\n") : target.aliases || "",
      status: target.status || "Active",
      threat_level: target.threat_level || "Normal",
      summary: target.summary || "",
      identifiers: Array.isArray(target.identifiers) ? target.identifiers.join("\n") : target.identifiers || "",
      vehicles: Array.isArray(target.vehicles) ? target.vehicles.join("\n") : target.vehicles || "",
      associated_regions: Array.isArray(target.associated_regions) ? target.associated_regions : getTargetCounties(target).filter((x) => x !== "Regional / Unknown"),
      associated_towns: Array.isArray(target.associated_towns) ? target.associated_towns : [],
      addresses: Array.isArray(target.addresses) ? target.addresses.join("\n") : target.addresses || "",
      associates: Array.isArray(target.associates) ? target.associates.join("\n") : target.associates || "",
      flags: Array.isArray(target.flags) ? target.flags : [],
      notes: target.notes || "",
      attachments: ensureArray(target.attachments),
    });
    setIsTargetFormOpen(true);
    setNotice("");
    setError("");
  }

  function closeTargetForm() {
    setEditingTargetId("");
    setTargetForm({ ...emptyTargetForm, associated_regions: [], associated_towns: [], flags: [] });
    setIsTargetFormOpen(false);
  }

  async function submitTargetProfile(e) {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!permissions.canCreateTargetProfile) {
      setError("Only Investigations or Admin users can create or edit subject profiles.");
      return;
    }


    const form = {
      ...emptyTargetForm,
      ...(targetForm || {}),
      associated_regions: ensureArray(targetForm?.associated_regions),
      associated_towns: ensureArray(targetForm?.associated_towns),
      flags: ensureArray(targetForm?.flags),
    };

    if (!String(form.name || "").trim()) {
      setError("Subject profile name is required.");
      return;
    }
    if (!String(form.status || "").trim()) {
      setError("Status is required.");
      return;
    }
    if (!String(form.threat_level || "").trim()) {
      setError("Risk level is required.");
      return;
    }
    if (!String(form.summary || "").trim()) {
      setError("Summary is required.");
      return;
    }

    const editorName = effectiveMe?.display_name || effectiveMe?.username || "Current User";
    const editTime = new Date().toISOString();
    const existing = editingTargetId ? ensureArray(targets).find((x) => x?.id === editingTargetId) : null;
    const baseHistory = Array.isArray(existing?.edit_history) ? existing.edit_history : [];
    const editHistory = existing
      ? [
          {
            id: makeId("edit"),
            action: String(existing.summary || "") !== String(form.summary || "").trim() ? "Summary/profile edited" : "Profile edited",
            by: editorName,
            at: editTime,
          },
          ...baseHistory,
        ]
      : [
          {
            id: makeId("edit"),
            action: "Profile created",
            by: editorName,
            at: editTime,
          },
        ];

    const profile = {
      ...(existing || {}),
      id: existing?.id || makeId("target"),
      name: String(form.name || "").trim(),
      aliases: splitLines(form.aliases),
      status: form.status,
      threat_level: form.threat_level,
      summary: String(form.summary || "").trim(),
      identifiers: splitLines(form.identifiers),
      vehicles: splitLines(form.vehicles),
      associated_regions: ensureArray(form.associated_regions),
      associated_towns: ensureArray(form.associated_towns),
      addresses: splitLines(form.addresses),
      associates: splitLines(form.associates),
      flags: ensureArray(form.flags),
      notes: String(form.notes || "").trim(),
      attachments: ensureArray(form.attachments),
      created_by_name: existing?.created_by_name || editorName,
      created_by_agency: existing?.created_by_agency || effectiveMe?.agency_id || "",
      created_at: existing?.created_at || editTime,
      updated_by_name: editorName,
      updated_at: editTime,
      edit_history: editHistory,
    };

    const nextTargets = existing
      ? ensureArray(targets).map((target) => (target?.id === profile.id ? profile : target))
      : [profile, ...ensureArray(targets)];

    setTargets(nextTargets);
    saveLocalTargets(nextTargets);
    setSelectedTargetId(profile.id);
    setTargetForm({ ...emptyTargetForm, associated_regions: [], associated_towns: [], flags: [], attachments: [] });
    setEditingTargetId("");
    setIsTargetFormOpen(false);
    setNotice(existing ? "Subject profile updated. Edit history saved." : "Subject profile saved.");
    logAction(existing ? "Subject profile edited" : "Subject profile created", profile.name);

    try {
      await api(existing ? `/api/targets/${encodeURIComponent(profile.id)}` : "/api/targets", {
        method: existing ? "PUT" : "POST",
        body: JSON.stringify(profile),
      });
    } catch {
      // Backend endpoint is optional for this upgrade. Local browser storage keeps the UI usable now.
    }
  }

  function deleteLocalTarget(targetId) {
    if (!permissions.canDeleteOrDeactivate) {
      setError("Only Investigations or Admin users can remove target profiles.");
      return;
    }
    const nextTargets = ensureArray(targets).filter((x) => x?.id !== targetId);
    setTargets(nextTargets);
    saveLocalTargets(nextTargets);
    setSelectedTargetId(nextTargets[0]?.id || "");
    logAction("Subject profile removed", `Target ID: ${targetId}`);
    setNotice("Target profile removed from local storage.");
  }

  async function submitOperation(e) {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!permissions.canCreateOperation) {
      setError("Only Investigations or Admin users can create operations.");
      return;
    }


    const form = { ...emptyOperationForm, ...(operationForm || {}), linked_target_ids: ensureArray(operationForm?.linked_target_ids) };

    if (!textValue(form.title).trim()) {
      setError("Operation title is required.");
      return;
    }

    const linkedTargets = ensureArray(form.linked_target_ids)
      .map((id) => ensureArray(targets).find((target) => target.id === id))
      .filter(Boolean)
      .map((target) => ({ id: target.id, name: target.name, threat_level: target.threat_level }));

    const op = {
      id: makeId("op"),
      ...form,
      linked_targets: linkedTargets,
      team: splitLines(form.team),
      equipment: splitLines(form.equipment),
      safety: splitLines(form.safety),
      attachments: ensureArray(form.attachments),
      created_by_name: effectiveMe?.display_name || effectiveMe?.username || "Current User",
      created_by_agency: effectiveMe?.agency_id || "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    try {
      const data = await api("/api/operations", {
        method: "POST",
        body: JSON.stringify(op),
      });
      const saved = data.operation || data.op || op;
      const nextOps = [saved, ...ensureArray(operations)];
      setOperations(nextOps);
      saveLocalOps(nextOps);
      setOperationForm(emptyOperationForm);
      setNotice("Operation saved.");
    } catch {
      const nextOps = [op, ...ensureArray(operations)];
      setOperations(nextOps);
      saveLocalOps(nextOps);
      setOperationForm(emptyOperationForm);
      setNotice("Operation saved locally. Add /api/operations later to sync across agencies.");
    }
  }

  function updateOperationStatus(opId, status) {
    const nextOps = ensureArray(operations).map((op) =>
      op.id === opId ? { ...op, status, updated_at: new Date().toISOString() } : op
    );
    setOperations(nextOps);
    saveLocalOps(nextOps);
  }

  function deleteLocalOperation(opId) {
    if (!permissions.canDeleteOrDeactivate) {
      setError("Only Investigations or Admin users can remove operations.");
      return;
    }
    const nextOps = ensureArray(operations).filter((op) => op?.id !== opId);
    setOperations(nextOps);
    saveLocalOps(nextOps);
    logAction("Operation removed", `Operation ID: ${opId}`);
    setNotice("Operation removed from local storage.");
  }

  function toggleOfficerSafetyRegion(regionName) {
    setOfficerSafetyForm((prev) => {
      const current = ensureArray(prev?.regions);
      const exists = current.includes(regionName);
      return { ...(prev || emptyOfficerSafetyForm), regions: exists ? current.filter((x) => x !== regionName) : [...current, regionName] };
    });
  }

  function toggleOfficerSafetyAgency(agencyId) {
    setOfficerSafetyForm((prev) => {
      const current = ensureArray(prev?.agencies);
      const exists = current.includes(agencyId);
      return { ...(prev || emptyOfficerSafetyForm), agencies: exists ? current.filter((x) => x !== agencyId) : [...current, agencyId] };
    });
  }

  function submitOfficerSafetyAlert(e) {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!permissions.canCreateOfficerSafety) {
      setError("Only Investigations or Admin users can create officer safety alerts.");
      return;
    }

    const form = { ...emptyOfficerSafetyForm, ...(officerSafetyForm || {}), regions: ensureArray(officerSafetyForm?.regions), agencies: ensureArray(officerSafetyForm?.agencies) };
    if (!String(form.title || "").trim()) return setError("Officer safety alert title is required.");
    if (!String(form.category || "").trim()) return setError("Officer safety category is required.");
    if (!String(form.riskLevel || "").trim()) return setError("Risk level is required.");
    if (!String(form.summary || "").trim()) return setError("Summary is required.");
    const linkedTarget = form.linkedTargetId ? ensureArray(targets).find((target) => target?.id === form.linkedTargetId) : null;
    const alert = {
      id: makeId("officer_safety"),
      title: String(form.title || "").trim(),
      subjectName: String(form.subjectName || linkedTarget?.name || "").trim(),
      category: form.category,
      riskLevel: form.riskLevel,
      summary: String(form.summary || "").trim(),
      regions: ensureArray(form.regions),
      agencies: ensureArray(form.agencies),
      linkedTargetId: form.linkedTargetId || "",
      vehicle: String(form.vehicle || "").trim(),
      location: String(form.location || "").trim(),
      expiresAt: form.expiresAt || "",
      attachments: ensureArray(form.attachments),
      isActive: true,
      createdBy: me?.display_name || me?.displayName || me?.username || "Current User",
      createdByAgency: me?.agency_id || me?.agency || "",
      createdAt: new Date().toISOString(),
    };
    const nextAlerts = [alert, ...ensureArray(officerSafetyAlerts)];
    setOfficerSafetyAlerts(nextAlerts);
    saveLocalOfficerSafetyAlerts(nextAlerts);
    setOfficerSafetyForm(emptyOfficerSafetyForm);
    logAction("Officer safety alert created", alert.title);
    setNotice("Officer safety alert created.");
    setNav("dashboard");
  }

  function deactivateOfficerSafetyAlert(alertId) {
    if (!permissions.canDeleteOrDeactivate) {
      setError("Only Investigations or Admin users can deactivate officer safety alerts.");
      return;
    }
    const nextAlerts = ensureArray(officerSafetyAlerts).map((alert) => alert?.id === alertId ? { ...alert, isActive: false, updatedAt: new Date().toISOString() } : alert);
    setOfficerSafetyAlerts(nextAlerts);
    saveLocalOfficerSafetyAlerts(nextAlerts);
    logAction("Officer safety alert deactivated", `Alert ID: ${alertId}`);
    setNotice("Officer safety alert deactivated.");
  }

  function submitTimelineEvent(e) {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!textValue(timelineForm?.title).trim()) {
      setError("Timeline title is required.");
      return;
    }

    const event = {
      id: makeId("timeline"),
      ...(timelineForm || {}),
      attachments: ensureArray(timelineForm?.attachments),
      created_by_name: effectiveMe?.display_name || effectiveMe?.username || "Current User",
      created_by_agency: effectiveMe?.agency_id || "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const nextEvents = [event, ...ensureArray(timelineEvents)];
    setTimelineEvents(nextEvents);
    saveLocalTimeline(nextEvents);
    setTimelineForm(emptyTimelineForm);
    logAction("Timeline event created", event.title);
    setNotice("Timeline event saved locally. Add /api/timeline later to sync across agencies.");
  }

  function deleteTimelineEvent(eventId) {
    if (!permissions.canDeleteOrDeactivate) {
      setError("Only Investigations or Admin users can remove timeline events.");
      return;
    }
    const nextEvents = ensureArray(timelineEvents).filter((event) => event?.id !== eventId);
    setTimelineEvents(nextEvents);
    saveLocalTimeline(nextEvents);
    logAction("Timeline event removed", `Timeline ID: ${eventId}`);
    setNotice("Timeline event removed from local storage.");
  }

  async function submitMessage(e) {
    e.preventDefault();
    if (!selectedChatUser || !textValue(messageBody).trim()) return;
    setError("");
    try {
      await api("/api/messages", {
        method: "POST",
        body: JSON.stringify({
          recipient_id: selectedChatUser.id,
          body: messageBody,
        }),
      });
      setMessageBody("");
      logAction("Message sent", `To: ${selectedChatUser.display_name || selectedChatUser.id}`);
      await openConversation(selectedChatUser);
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  async function submitRegion(e) {
    e.preventDefault();
    setError("");
    const region = {
      id: String(newRegion.id || newRegion.name || makeId("region")).trim().replace(/\s+/g, "_").toUpperCase(),
      name: String(newRegion.name || newRegion.id || "New Region").trim(),
      description: String(newRegion.description || "").trim(),
      member_count: 0,
    };
    if (!region.name) {
      setError("Region name is required.");
      return;
    }

    let saved = region;
    try {
      const data = await api("/api/regions", {
        method: "POST",
        body: JSON.stringify(region),
      });
      saved = data.region || region;
    } catch {
      // Backend endpoint is optional during MVP. Save locally instead of showing Request failed.
    }

    const byId = new Map([...safeRegions, saved].map((r) => [String(r.id || r.name).toUpperCase(), r]));
    const nextRegions = Array.from(byId.values());
    setRegions(nextRegions);
    saveLocalRegions(nextRegions);
    setNewRegion({ id: "", name: "", description: "" });
    logAction("Region created", saved.name || saved.id);
    setNotice("Region saved locally. Backend sync can be added later.");
  }

  function markDashboardAlertSeen(alertId) {
    setSeenDashboardAlertIds((prev) => {
      const next = Array.from(new Set([...ensureArray(prev), alertId]));
      saveSeenDashboardAlerts(next);
      return next;
    });
  }

  function markAllDashboardAlertsSeen() {
    setSeenDashboardAlertIds((prev) => {
      const next = Array.from(new Set([...ensureArray(prev), ...dashboardAlertItems.map((item) => item.id)]));
      saveSeenDashboardAlerts(next);
      return next;
    });
  }

  function acceptDisclosure() {
    try {
      localStorage.setItem(DISCLOSURE_STORAGE_KEY, "accepted");
    } catch {
      // If storage is unavailable, still allow the user into the app for this session.
    }
    setShowDisclosure(false);
  }

  if (loading) {
    return (
      <div style={shellStyle}>
        <div style={{ color: "#dce6ef", fontSize: 18 }}>Loading SPEAR...</div>
      </div>
    );
  }

  return (
    <div style={shellStyle}>
      {showDisclosure ? (
        <div style={disclosureOverlayStyle}>
          <div style={disclosureModalStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 13, color: "#91a6bb", fontWeight: 800, letterSpacing: 1 }}>SPEAR DISCLOSURE</div>
                <h2 style={{ margin: "6px 0 8px", fontSize: 24 }}>Information Sharing Notice</h2>
              </div>
              <Badge tone="warning">Required</Badge>
            </div>

            <div style={{ color: "#dce6ef", lineHeight: 1.55, fontSize: 14 }}>
              <p>
                SPEAR is a regional information-sharing and situational-awareness platform. It is not intended to operate as a criminal intelligence database, criminal history repository, RMS/CAD replacement, NCIC/CCIC interface, or CJIS-compliant records system.
              </p>

              <p>
                SPEAR has not been represented as compliant with CJIS Security Policy or 28 CFR Part 23. Users are prohibited from entering, uploading, copying, or redistributing information obtained directly from CCIC, NCIC, CJIS systems, criminal history returns, protected RMS/CAD records, sealed records, juvenile protected information, or any other restricted law-enforcement database unless specifically authorized by agency policy and applicable law.
              </p>

              <p>
                Information posted in SPEAR should be limited to lawful, agency-authorized, operationally relevant information such as officer-safety notices, BOLO-style advisories, situational updates, regional coordination notes, and field-observed information. Users remain responsible for verifying information through official systems before taking enforcement action.
              </p>

              <p>
                Do not post sensitive personal identifiers, criminal history data, NCIC/CCIC hit details, protected victim information, confidential-source identifiers, or tactical details that your agency has not authorized for regional sharing. All use may be subject to agency review, audit, disclosure, retention rules, and applicable public-records or discovery obligations.
              </p>

              <p style={{ color: "#ffcf8a", fontWeight: 800 }}>
                By continuing, you acknowledge that you understand these restrictions and agree to use SPEAR only for authorized regional information sharing.
              </p>
            </div>

            <button type="button" onClick={acceptDisclosure} style={{ ...primaryButtonStyle, width: "100%", marginTop: 12 }}>
              I Understand and Agree
            </button>
          </div>
        </div>
      ) : null}
      <div style={sidebarStyle}>
        <div
          style={{
            marginBottom: 22,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src="/spear-logo.png"
            alt="SPEAR"
            style={{
              width: "185px",
              height: "auto",
              objectFit: "contain",
              display: "block",
              marginBottom: 8,
            }}
          />
          <div
            style={{
              color: "#c6d1dc",
              fontSize: 10,
              lineHeight: 1.35,
              textAlign: "center",
              fontWeight: 800,
              letterSpacing: 0.7,
              textTransform: "uppercase",
            }}
          >
            Situational Platform for Enhanced Awareness and Response
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <button
            type="button"
            onClick={() => setNotificationCenterOpen(true)}
            style={{
              width: "100%",
              marginBottom: 14,
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid #3c5168",
              background: "#18222d",
              color: "#ecf3f9",
              cursor: "pointer",
              fontWeight: 800,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Notification Center</span>
            <span
              style={{
                minWidth: 28,
                height: 28,
                borderRadius: 999,
                background: unreadNotifications.length ? "#a61d2d" : "#26313d",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 900,
                padding: "0 8px",
              }}
            >
              {unreadNotifications.length}
            </span>
          </button>

          <div style={{ fontSize: 13, color: "#94a6b8" }}>Logged in as</div>
          <div style={{ fontWeight: 700 }}>{effectiveMe?.display_name || effectiveMe?.username || "User"}</div>
          <div style={{ color: "#b6c6d6", fontSize: 13 }}>
            {permissions.roleLabel || "Role"}{effectiveMe?.agency_id ? ` • ${effectiveMe.agency_id}` : ""}
          </div>
        </div>

        <div>
          {NAV.filter((item) => canViewNavItem(effectiveMe, item)).map((item) => (
            <button
              key={item.key}
              onClick={() => setNav(item.key)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px 14px",
                marginBottom: 8,
                borderRadius: 12,
                border: "1px solid #26313d",
                background: nav === item.key ? "#223142" : "#12171d",
                color: "#ecf3f9",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div style={mainStyle}>
        {notificationCenterOpen ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.72)",
              zIndex: 9999,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              padding: 24,
              overflowY: "auto",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: 920,
                background: "#12171d",
                border: "1px solid #2b3948",
                borderRadius: 18,
                padding: 20,
                marginTop: 20,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 900 }}>Notification Center</div>
                  <div style={{ color: "#9fb1c3", marginTop: 4 }}>Unread regional intelligence and operational updates.</div>
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  <button type="button" style={secondaryButtonStyle} onClick={markAllNotificationsRead}>Mark All Read</button>
                  <button type="button" style={primaryButtonStyle} onClick={() => setNotificationCenterOpen(false)}>Close</button>
                </div>
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                {!unreadNotifications.length ? (
                  <Card>
                    <div style={{ fontWeight: 800, fontSize: 18 }}>No unread notifications.</div>
                  </Card>
                ) : (
                  unreadNotifications.map((note) => (
                    <Card key={note.id} accent>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                            <Badge tone="critical">{note.category}</Badge>
                            <Badge tone="normal">{note.source}</Badge>
                          </div>

                          <div style={{ fontSize: 20, fontWeight: 900 }}>{note.title}</div>
                          <div style={{ color: "#c2cfdb", marginTop: 8, lineHeight: 1.45 }}>
                            {note.subtitle || "No additional details provided."}
                          </div>
                          <div style={{ marginTop: 10, fontSize: 12, color: "#8da2b7" }}>
                            {formatDateTime(note.createdAt)}
                          </div>
                        </div>

                        <button type="button" style={secondaryButtonStyle} onClick={() => markNotificationRead(note.id)}>
                          Mark Read
                        </button>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}

        {error ? <Alert tone="error">{error}</Alert> : null}
        {notice ? <Alert tone="notice">{notice}</Alert> : null}

        {nav === "dashboard" && (
          <>
            <SectionTitle
              title="Dashboard"
              subtitle="New unseen alerts for your subscribed region(s)."
              right={
                dashboardAlertItems.length ? (
                  <button type="button" style={secondaryButtonStyle} onClick={markAllDashboardAlertsSeen}>
                    Mark All Seen
                  </button>
                ) : (
                  <Badge tone="low">All caught up</Badge>
                )
              }
            />


            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>Access Level</div>
                  <div style={{ color: "#9eb0c3", fontSize: 13, marginTop: 4 }}>
                    Patrol users are limited to assigned regions/areas. Investigations and Admin users can access investigative workflows.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Badge tone={permissions.isAdmin ? "critical" : permissions.isSupervisor ? "warning" : "normal"}>
                    {permissions.roleLabel}
                  </Badge>
                  <Badge tone={permissions.canCreateOfficerSafety ? "low" : "default"}>
                    Officer Safety: {permissions.canCreateOfficerSafety ? "Create" : "View Assigned"}
                  </Badge>
                  <Badge tone={permissions.canCreateTargetProfile ? "low" : "default"}>
                    Profiles: {permissions.canViewSubjectProfiles ? "Investigations" : "No Access"}
                  </Badge>
                </div>
              </div>
            </Card>

            {ensureArray(subscribedOfficerSafetyAlerts).length ? (
              <Card accent>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 950, fontSize: 20, color: "#ffd7dc" }}>
                      Pinned Officer Safety Alerts
                    </div>
                    <div style={{ color: "#e7a7af", fontSize: 13, marginTop: 4 }}>
                      Active officer safety notices for your subscribed region(s).
                    </div>
                  </div>
                  <Badge tone="critical">{ensureArray(subscribedOfficerSafetyAlerts).length} active</Badge>
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  {ensureArray(subscribedOfficerSafetyAlerts).slice(0, 5).map((alert) => (
                    <div
                      key={alert?.id}
                      style={{
                        padding: 14,
                        borderRadius: 14,
                        border: "1px solid #962535",
                        background: "linear-gradient(135deg,#5c1017,#25070b)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                        <div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <Badge tone="critical">{alert?.riskLevel || "Critical"}</Badge>
                            <Badge tone="warning">{alert?.category || "Officer Safety"}</Badge>
                            <span style={{ color: "#ffd7dc", fontSize: 12, fontWeight: 800 }}>
                              {ensureArray(getOfficerSafetyRegions(alert)).join(", ") || "Regional"}
                            </span>
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 950, marginTop: 8 }}>{alert?.title}</div>
                          {alert?.subjectName ? (
                            <div style={{ color: "#ffd7dc", fontWeight: 900, marginTop: 4 }}>
                              Subject: {alert.subjectName}
                            </div>
                          ) : null}
                          <div style={{ color: "#f0c7cc", fontSize: 13, marginTop: 6, lineHeight: 1.45 }}>
                            {alert?.summary}
                          </div>
                          <AttachmentPreviewStrip attachments={alert?.attachments} />
                          <div style={{ color: "#d38a94", fontSize: 12, marginTop: 6 }}>
                            Created {formatDateTime(alert?.createdAt)}
                          </div>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 105 }}>
                          {alert?.linkedTargetId ? (
                            <button
                              type="button"
                              style={secondaryButtonStyle}
                              onClick={() => {
                                setSelectedTargetId(alert.linkedTargetId);
                                setNav("targets");
                              }}
                            >
                              Open Target
                            </button>
                          ) : null}
                          <button
                            type="button"
                            style={secondaryButtonStyle}
                            onClick={() => {
                              setOfficerSafetyPanelMode("list");
                              setNav("officer-safety");
                            }}
                          >
                            View All
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}


            <Card accent>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                {[
                  "Officer Safety",
                  "Critical Alerts",
                  "Target Profiles",
                  "Information Postings",
                  "Active Ops",
                  "Timeline Events",
                ].map((category) => (
                  <div
                    key={category}
                    style={{
                      padding: 14,
                      borderRadius: 14,
                      border: "1px solid #2d3b49",
                      background: dashboardAlertCounts[category] ? "#1a2430" : "#10161b",
                    }}
                  >
                    <div style={{ color: "#9eb0c3", fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
                      {category}
                    </div>
                    <div style={{ fontSize: 30, fontWeight: 950, marginTop: 6 }}>
                      {dashboardAlertCounts[category]}
                    </div>
                    <div style={{ color: "#9eb0c3", fontSize: 12, marginTop: 4 }}>
                      new unseen
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <RegionAlertDashboard
              alerts={subscribedAlerts}
              alertSubscriptions={alertSubscriptions}
              alertRegionOptions={alertRegionOptions}
              onToggleSubscription={toggleAlertSubscription}
              onOpenFeed={() => setNav("intel")}
            />

            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 950, fontSize: 20 }}>New Regional Alerts</div>
                  <div style={{ color: "#9eb0c3", fontSize: 13, marginTop: 3 }}>
                    Showing only unseen items tied to your subscribed regions and operational categories.
                  </div>
                </div>
                <Badge tone={dashboardAlertItems.length ? "warning" : "low"}>
                  {dashboardAlertItems.length} new
                </Badge>
              </div>

              {dashboardAlertItems.length ? (
                <div style={{ display: "grid", gap: 10 }}>
                  {dashboardAlertItems.slice(0, 15).map((item) => (
                    <div
                      key={item.id}
                      style={{
                        border: "1px solid #26313d",
                        borderRadius: 14,
                        background: "#10161b",
                        padding: 12,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                        <div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <Badge tone={item.category === "Critical Alerts" ? "critical" : item.category === "Active Ops" ? "high" : "normal"}>
                              {item.category}
                            </Badge>
                            <span style={{ color: "#9eb0c3", fontSize: 12, fontWeight: 800 }}>
                              {item.region || "Subscribed regions"}
                            </span>
                          </div>
                          <div style={{ fontWeight: 950, fontSize: 16, marginTop: 8 }}>{item.title}</div>
                          <div style={{ color: "#c8d6e2", fontSize: 13, marginTop: 5, lineHeight: 1.4 }}>
                            {item.detail}
                          </div>
                          <div style={{ color: "#7f91a3", fontSize: 12, marginTop: 6 }}>
                            {formatDateTime(item.date)}
                          </div>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 110 }}>
                          <button
                            type="button"
                            style={secondaryButtonStyle}
                            onClick={item.action}
                          >
                            Open
                          </button>
                          <button
                            type="button"
                            style={secondaryButtonStyle}
                            onClick={() => markDashboardAlertSeen(item.id)}
                          >
                            Mark Seen
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: "#9eb0c3", padding: "14px 0" }}>
                  No new unseen alerts for your subscribed regions.
                </div>
              )}
            </Card>
          </>
        )}

        {nav === "officer-safety" && (
          <>
            <SectionTitle
              title="Officer Safety"
              subtitle={officerSafetyPanelMode === "create" ? "Create a high-priority officer safety alert." : "Review active high-priority officer safety alerts."}
              right={
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <Badge tone="critical">{ensureArray(subscribedOfficerSafetyAlerts).length} active</Badge>
                  {officerSafetyPanelMode === "create" ? (
                    <button type="button" style={secondaryButtonStyle} onClick={() => setOfficerSafetyPanelMode("list")}>View Active Alerts</button>
                  ) : (
                    permissions.canCreateOfficerSafety ? (
                      <button type="button" style={{ ...primaryButtonStyle, background: "linear-gradient(135deg,#8b1e2d,#51111a)" }} onClick={() => setOfficerSafetyPanelMode("create")}>Create New Alert</button>
                    ) : (
                      <Badge tone="default">View Only</Badge>
                    )
                  )}
                </div>
              }
            />

            {officerSafetyPanelMode === "list" ? (
              <Card accent>
                <div style={{ fontWeight: 950, fontSize: 22, marginBottom: 6 }}>Active Officer Safety Alerts</div>
                <div style={{ color: "#9eb0c3", fontSize: 13, marginBottom: 14 }}>
                  These are the current active officer safety notices, including pinned dashboard alerts.
                </div>

                {ensureArray(officerSafetyAlerts).filter((alert) => alert?.isActive !== false).length ? (
                  <div style={{ display: "grid", gap: 12 }}>
                    {ensureArray(officerSafetyAlerts).filter((alert) => alert?.isActive !== false).map((alert) => (
                      <div key={alert?.id || alert?.createdAt || alert?.title} style={{ padding: 16, borderRadius: 14, border: "1px solid #962535", background: "linear-gradient(135deg,#3b0c12,#160c10)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                              <Badge tone="critical">{alert?.riskLevel || "Critical"}</Badge>
                              <Badge tone="warning">{alert?.category || "Officer Safety"}</Badge>
                              <Badge tone="default">{ensureArray(getOfficerSafetyRegions(alert)).join(", ") || "Regional"}</Badge>
                            </div>

                            <div style={{ fontWeight: 950, fontSize: 20, marginTop: 10 }}>{alert?.title || "Officer Safety Alert"}</div>

                            {alert?.subjectName ? <div style={{ color: "#ffd7dc", fontWeight: 900, marginTop: 6 }}>Subject: {alert.subjectName}</div> : null}
                            {alert?.vehicle ? <div style={{ color: "#dce6ef", marginTop: 4 }}>Vehicle / Identifier: {alert.vehicle}</div> : null}
                            {alert?.location ? <div style={{ color: "#dce6ef", marginTop: 4 }}>Location / Area: {alert.location}</div> : null}

                            <div style={{ color: "#c8d6e2", marginTop: 8, lineHeight: 1.5 }}>{alert?.summary || "No summary entered."}</div>

                            <AttachmentPreviewStrip attachments={alert?.attachments} />

                            <div style={{ color: "#9eb0c3", fontSize: 12, marginTop: 8 }}>
                              Created {formatDateTime(alert?.createdAt)}
                              {alert?.createdBy ? ` by ${alert.createdBy}` : ""}
                              {alert?.createdByAgency ? ` • ${alert.createdByAgency}` : ""}
                              {alert?.expiresAt ? ` • Expires ${formatDateTime(alert.expiresAt)}` : ""}
                            </div>
                          </div>

                          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 130 }}>
                            {alert?.linkedTargetId ? (
                              <button type="button" style={secondaryButtonStyle} onClick={() => { setSelectedTargetId(alert.linkedTargetId); setNav("targets"); }}>
                                Open Target
                              </button>
                            ) : null}

                            <button type="button" style={secondaryButtonStyle} onClick={() => deactivateOfficerSafetyAlert(alert?.id)}>
                              Deactivate
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "#9eb0c3" }}>No active officer safety alerts.</div>
                )}
              </Card>
            ) : (
              permissions.canCreateOfficerSafety ? (
              <Card accent>
                <form onSubmit={submitOfficerSafetyAlert}>
                  <Label>Alert Title *</Label>
                  <Input value={officerSafetyForm.title} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), title: e.target.value }))} />

                  <div style={twoColStyle}>
                    <div>
                      <Label>Category *</Label>
                      <Select value={officerSafetyForm.category} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), category: e.target.value }))}>
                        {SAFETY_ALERT_TYPES.map((type) => <option key={type}>{type}</option>)}
                      </Select>
                    </div>
                    <div>
                      <Label>Risk Level *</Label>
                      <Select value={officerSafetyForm.riskLevel} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), riskLevel: e.target.value }))}>
                        {PRIORITIES.map((level) => <option key={level}>{level}</option>)}
                      </Select>
                    </div>
                  </div>

                  <Label>Linked Subject Profile</Label>
                  <Select value={officerSafetyForm.linkedTargetId} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), linkedTargetId: e.target.value }))}>
                    <option value="">No linked subject</option>
                    {ensureArray(targets).map((target) => <option key={target?.id} value={target?.id}>{target?.name || "Unnamed Subject"}</option>)}
                  </Select>

                  <Label>Subject Name / Descriptor</Label>
                  <Input value={officerSafetyForm.subjectName} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), subjectName: e.target.value }))} />

                  <Label>Summary *</Label>
                  <TextArea rows={5} value={officerSafetyForm.summary} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), summary: e.target.value }))} />

                  <div style={twoColStyle}>
                    <div>
                      <Label>Vehicle / Identifier</Label>
                      <Input value={officerSafetyForm.vehicle} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), vehicle: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Location / Area</Label>
                      <Input value={officerSafetyForm.location} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), location: e.target.value }))} />
                    </div>
                  </div>

                  <Label>Share to Regions</Label>
                  <div style={chipWrapStyle}>
                    {DEFAULT_ALERT_REGIONS.map((regionName) => (
                      <Chip key={regionName} selected={safeIncludes(officerSafetyForm.regions, regionName)} onClick={() => toggleOfficerSafetyRegion(regionName)}>{regionName}</Chip>
                    ))}
                  </div>

                  <Label>Share to Agencies</Label>
                  <div style={chipWrapStyle}>
                    {safeAgencies.map((agency) => (
                      <Chip key={agency?.id} selected={safeIncludes(officerSafetyForm.agencies, agency?.id)} onClick={() => toggleOfficerSafetyAgency(agency?.id)}>{agency?.short_name || agency?.name || agency?.id}</Chip>
                    ))}
                  </div>

                  <Label>Expires At</Label>
                  <Input type="datetime-local" value={officerSafetyForm.expiresAt} onChange={(e) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), expiresAt: e.target.value }))} />

                  <AttachmentManager
                    attachments={ensureArray(officerSafetyForm.attachments)}
                    onChange={(attachments) => setOfficerSafetyForm((prev) => ({ ...(prev || emptyOfficerSafetyForm), attachments }))}
                    label="Officer Safety Attachments / Photos"
                  />

                  <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button type="submit" style={{ ...primaryButtonStyle, background: "linear-gradient(135deg,#8b1e2d,#51111a)" }}>Create Officer Safety Alert</button>
                    <button type="button" style={secondaryButtonStyle} onClick={() => setOfficerSafetyPanelMode("list")}>Cancel / View Active Alerts</button>
                  </div>
                </form>
              </Card>
              ) : (
                <PermissionNotice>Only Investigations or Admin users can create officer safety alerts.</PermissionNotice>
              )
            )}
          </>
        )}

        {nav === "intel" && (
          <>
            <SectionTitle
              title="Regional Feed"
              subtitle="Search and filter posts shared to your agency, regions, or directly visible to you."
              right={<Badge tone="target">{ensureArray(filteredIntel).length} visible</Badge>}
            />
            <Card>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 180px 220px", gap: 12 }}>
                <Input
                  placeholder="Search title, summary, body, agency, creator..."
                  value={intelSearch}
                  onChange={(e) => setIntelSearch(e.target.value)}
                />
                <Select value={intelPriority} onChange={(e) => setIntelPriority(e.target.value)}>
                  <option>All</option>
                  {PRIORITIES.map((x) => <option key={x}>{x}</option>)}
                </Select>
                <Select value={intelType} onChange={(e) => setIntelType(e.target.value)}>
                  <option>All</option>
                  {POST_TYPES.map((x) => <option key={x}>{x}</option>)}
                </Select>
              </div>
            </Card>
            {ensureArray(filteredIntel).map((item) => (
              <IntelCard key={item?.id || makeId("map-item")} item={item} />
            ))}
            {!ensureArray(filteredIntel).length && <Card>No matching information available.</Card>}
          </>
        )}

        {nav === "create" && (
          <>
            <SectionTitle
              title="Create Information"
              subtitle="Share a BOLO, officer safety alert, information release, or regional bulletin."
            />
            <Card>
              <form onSubmit={submitIntel}>
                <Label>Title</Label>
                <Input
                  value={safeIntelForm.title}
                  onChange={(e) => setIntelForm((prev) => ({ ...(prev || {}), title: e.target.value }))}
                />

                <div style={twoColStyle}>
                  <div>
                    <Label>Post Type</Label>
                    <Select
                      value={safeIntelForm.post_type}
                      onChange={(e) => setIntelForm((prev) => ({ ...(prev || {}), post_type: e.target.value }))}
                    >
                      {POST_TYPES.map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={safeIntelForm.priority}
                      onChange={(e) => setIntelForm((prev) => ({ ...(prev || {}), priority: e.target.value }))}
                    >
                      {PRIORITIES.map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                <Label>Summary *</Label>
                <Input
                  value={safeIntelForm.summary}
                  onChange={(e) => setIntelForm((prev) => ({ ...(prev || {}), summary: e.target.value }))}
                />

                <Label>Body</Label>
                <TextArea
                  rows={8}
                  value={safeIntelForm.body}
                  onChange={(e) => setIntelForm((prev) => ({ ...(prev || {}), body: e.target.value }))}
                />

                <Label>Linked Subject Profiles</Label>
                <div style={chipWrapStyle}>
                  {ensureArray(targets).map((target) => (
                    <Chip
                      key={target?.id || makeId("target-key")}
                      selected={safeIdSelected(safeIntelForm, "linked_target_ids", target)}
                      onClick={() => target?.id && toggleIntelLinkedTarget(target.id)}
                    >
                      {target?.name || "Unnamed Subject"}
                    </Chip>
                  ))}
                  {!ensureArray(targets).length && <div style={{ color: "#9eb0c3", fontSize: 13 }}>No target profiles created yet.</div>}
                </div>

                <Label>Expires At (optional)</Label>
                <Input
                  type="datetime-local"
                  value={safeIntelForm.expires_at}
                  onChange={(e) => setIntelForm((prev) => ({ ...(prev || {}), expires_at: e.target.value }))}
                />

                <AttachmentManager
                  attachments={safeIntelForm.attachments}
                  onChange={(attachments) => setIntelForm((prev) => ({ ...(prev || {}), attachments }))}
                  label="Intel Attachments / Photos"
                />

                <div style={{ marginTop: 18, marginBottom: 8, fontWeight: 800 }}>
                  Share to Agencies
                </div>
                <div style={chipWrapStyle}>
                  {safeAgencies.map((agency) => {
                    const selected = safeSome(safeIntelForm.targets,
                      (t) => t?.target_type === "agency" && t?.target_id === agency?.id
                    );
                    return (
                      <Chip
                        key={agency?.id}
                        selected={selected}
                        onClick={() => toggleTarget("agency", agency?.id)}
                      >
                        {agency.short_name || agency.name || agency?.id}
                      </Chip>
                    );
                  })}
                </div>

                <div style={{ marginTop: 18, marginBottom: 8, fontWeight: 800 }}>
                  Share to Regions
                </div>
                <div style={chipWrapStyle}>
                  {safeRegions.map((region) => {
                    const selected = safeSome(safeIntelForm.targets,
                      (t) => t?.target_type === "region" && t?.target_id === region?.id
                    );
                    return (
                      <Chip
                        key={region?.id}
                        selected={selected}
                        onClick={() => toggleTarget("region", region?.id)}
                      >
                        {region?.name || region?.id || "Region"}
                      </Chip>
                    );
                  })}
                </div>

                <div style={{ marginTop: 20 }}>
                  <button style={primaryButtonStyle} type="submit">
                    Submit Information
                  </button>
                </div>
              </form>
            </Card>
          </>
        )}

        {nav === "targets" && !permissions.canViewSubjectProfiles && (
          <PermissionNotice>Subject profiles are restricted to Investigations and Admin users.</PermissionNotice>
        )}

        {nav === "targets" && permissions.canViewSubjectProfiles && (
          <>
            <SectionTitle
              title="Subject Profiles"
              subtitle="Search, create, and edit subject profiles by associated regions. This page avoids DOB and CJIS/CCIC/NCIC-derived identifiers."
              right={<Badge tone="target">Region grouped</Badge>}
            />

            <Card accent>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 950, fontSize: 22 }}>Subject Profile Management</div>
                  <div style={{ color: "#9eb0c3", fontSize: 13, marginTop: 4 }}>
                    Profiles stay minimized. Open a county, expand a profile, or create a new profile.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => permissions.canCreateTargetProfile ? startNewTargetProfile() : setError("Only Investigations or Admin users can create subject profiles.")}
                  style={{
                    ...primaryButtonStyle,
                    fontSize: 16,
                    padding: "14px 18px",
                    borderRadius: 14,
                    fontWeight: 950,
                    boxShadow: "0 12px 28px rgba(69,123,157,0.22)",
                  }}
                >
                  + Create New Subject Profile
                </button>
              </div>
            </Card>

            {isTargetFormOpen ? (
              <Card accent>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 950, fontSize: 20 }}>{editingTargetId ? "Edit Subject Profile" : "Create New Subject Profile"}</div>
                    <div style={{ color: "#9eb0c3", fontSize: 12, marginTop: 4 }}>
                      Edits are locally logged with user/date/time. Do not enter DOB, SSN, SID/FBI numbers, CCIC/NCIC returns, DMV returns, or protected criminal history.
                    </div>
                  </div>
                  <button type="button" style={secondaryButtonStyle} onClick={closeTargetForm}>Close</button>
                </div>

                <form onSubmit={submitTargetProfile}>
                  <Label>Name / Primary Identifier *</Label>
                  <Input
                    value={textValue(safeTargetForm.name)}
                    onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), name: e.target.value }))}
                    placeholder="Example: John Doe / Unknown male / street name / alias"
                  />

                  <div style={twoColStyle}>
                    <div>
                      <Label>Status *</Label>
                      <Select
                        value={textValue(safeTargetForm.status) || "Active"}
                        onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), status: e.target.value }))}
                      >
                        <option>Active</option>
                        <option>Inactive</option>
                        <option>At Large</option>
                        <option>In Custody</option>
                        <option>Under Review</option>
                      </Select>
                    </div>
                    <div>
                      <Label>Risk Level *</Label>
                      <Select
                        value={textValue(safeTargetForm.threat_level) || "Normal"}
                        onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), threat_level: e.target.value }))}
                      >
                        {PRIORITIES.map((x) => <option key={x}>{x}</option>)}
                      </Select>
                    </div>
                  </div>

                  <Label>Associated Regions</Label>
                  <div style={{ display: "grid", gap: 10 }}>
                    {DEFAULT_ALERT_REGIONS.map((county) => {
                      const countyTowns = getTownsForCounty(county);
                      const selectedRegion = safeIncludes(safeTargetForm?.associated_regions, county);
                      const expanded = Boolean(expandedProfileRegions?.[county]);
                      const selectedTownCount = countyTowns.filter((place) => safeIncludes(safeTargetForm?.associated_towns, place.name)).length;
                      return (
                        <div key={county} style={{ border: selectedRegion ? "1px solid #6b95bf" : "1px solid #31404f", borderRadius: 12, overflow: "hidden", background: selectedRegion ? "#172638" : "#10161b" }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", padding: 10 }}>
                            <button
                              type="button"
                              onClick={() => toggleProfileRegionSelection(county)}
                              style={{ ...secondaryButtonStyle, flex: 1, textAlign: "left", background: selectedRegion ? "#27415d" : "#121a22" }}
                            >
                              {selectedRegion ? "✓ " : ""}{county} County · {selectedTownCount}/{countyTowns.length} towns selected
                            </button>
                            <button type="button" style={secondaryButtonStyle} onClick={() => toggleProfileRegionPanel(county)}>
                              {expanded ? "Collapse" : "Expand"}
                            </button>
                          </div>
                          {expanded ? (
                            <div style={{ padding: "0 10px 10px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                              {countyTowns.map((place) => {
                                const selectedTown = safeIncludes(safeTargetForm?.associated_towns, place.name);
                                return (
                                  <button
                                    key={place.id}
                                    type="button"
                                    onClick={() => toggleProfileTownSelection(place)}
                                    style={{
                                      textAlign: "left",
                                      padding: "8px 10px",
                                      borderRadius: 10,
                                      border: selectedTown ? "1px solid #6b95bf" : "1px solid #31404f",
                                      background: selectedTown ? "#27415d" : "#0d1319",
                                      color: "#fff",
                                      cursor: "pointer",
                                      fontWeight: 800,
                                      fontSize: 12,
                                    }}
                                  >
                                    {selectedTown ? "✓ " : ""}{place.name}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  <Label>Summary</Label>
                  <TextArea rows={4} value={textValue(safeTargetForm.summary)} onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), summary: e.target.value }))} />

                  <Label>Officer Safety / Risk Flags</Label>
                  <div style={chipWrapStyle}>
                    {THREAT_FLAGS.map((flag) => (
                      <Chip key={flag} selected={safeIncludes(safeTargetForm?.flags, flag)} onClick={() => toggleTargetFlag(flag)}>
                        {flag}
                      </Chip>
                    ))}
                  </div>

                  <Label>Aliases / Monikers</Label>
                  <TextArea rows={2} value={textValue(safeTargetForm.aliases)} onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), aliases: e.target.value }))} placeholder="One per line" />

                  <Label>Non-CJIS Descriptors</Label>
                  <TextArea rows={3} value={textValue(safeTargetForm.identifiers)} onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), identifiers: e.target.value }))} placeholder="Scars/tattoos, physical descriptors, social handles, nicknames. One per line." />

                  <Label>Vehicles</Label>
                  <TextArea rows={3} value={textValue(safeTargetForm.vehicles)} onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), vehicles: e.target.value }))} placeholder="Observed vehicle details. Do not copy protected DMV/CJIS return data. One per line." />

                  <Label>Known / Associated Locations</Label>
                  <TextArea rows={3} value={textValue(safeTargetForm.addresses)} onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), addresses: e.target.value }))} placeholder="Observed locations, hangouts, roadways, motels, etc. Do not copy CCIC/NCIC/DMV-derived addresses. One per line." />

                  <Label>Associates</Label>
                  <TextArea rows={3} value={textValue(safeTargetForm.associates)} onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), associates: e.target.value }))} placeholder="One per line" />

                  <Label>Operational Notes</Label>
                  <TextArea rows={4} value={textValue(safeTargetForm.notes)} onChange={(e) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), notes: e.target.value }))} />

                  <AttachmentManager
                    attachments={safeTargetForm.attachments}
                    onChange={(attachments) => setTargetForm((prev) => ({ ...(prev || emptyTargetForm), attachments }))}
                    label="Subject Profile Attachments / Photos"
                  />

                  <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button style={primaryButtonStyle} type="submit">{editingTargetId ? "Save Profile Updates" : "Save Subject Profile"}</button>
                    <button type="button" style={secondaryButtonStyle} onClick={closeTargetForm}>Cancel</button>
                  </div>
                </form>
              </Card>
            ) : null}

            <Card>
              <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>Search Profiles</div>
              <Input
                placeholder="Search subject, town, county, vehicle, location, associate, flag..."
                value={targetSearch}
                onChange={(e) => setTargetSearch(e.target.value)}
              />
              <div style={{ color: "#9eb0c3", fontSize: 12, marginTop: 8, lineHeight: 1.35 }}>
                Profiles appear under each selected region/county. Expand a profile to view all saved details.
              </div>
            </Card>

              {selectedTarget ? (
                <Card accent>
                  <div style={{ fontSize: 22, fontWeight: 900, marginBottom: 6 }}>
                    Target Intelligence Hub
                  </div>
                  <div style={{ color: "#9eb0c3", marginBottom: 14 }}>
                    Linked operational intelligence for {selectedTarget?.name || "selected target"}.
                  </div>

                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ border: "1px solid #4a2430", borderRadius: 12, padding: 12, background: "#171216" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ fontWeight: 900 }}>Officer Safety Alerts</div>
                        <Badge tone="critical">{relatedTargetOfficerSafety.length}</Badge>
                      </div>

                      {relatedTargetOfficerSafety.length ? relatedTargetOfficerSafety.map((item) => (
                        <div key={item?.id || item?.title} style={{ padding: "10px 0", borderTop: "1px solid #352029" }}>
                          <div style={{ fontWeight: 800 }}>{item?.title || "Officer Safety Alert"}</div>
                          <div style={{ color: "#dce6ef", marginTop: 6 }}>{item?.summary || "No summary."}</div>
                          <AttachmentPreviewStrip attachments={item?.attachments} />
                        </div>
                      )) : <div style={{ color: "#9eb0c3" }}>No linked officer safety alerts.</div>}
                    </div>

                    <div style={{ border: "1px solid #284056", borderRadius: 12, padding: 12, background: "#121920" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ fontWeight: 900 }}>Related Intelligence</div>
                        <Badge tone="normal">{relatedTargetIntel.length}</Badge>
                      </div>

                      {relatedTargetIntel.length ? relatedTargetIntel.map((item) => (
                        <div key={item?.id || item?.title} style={{ padding: "10px 0", borderTop: "1px solid #243545" }}>
                          <div style={{ fontWeight: 800 }}>{item?.title || "Intel Post"}</div>
                          <div style={{ color: "#dce6ef", marginTop: 6 }}>{item?.summary || "No summary."}</div>
                          <AttachmentPreviewStrip attachments={item?.attachments} />
                        </div>
                      )) : <div style={{ color: "#9eb0c3" }}>No linked intelligence posts.</div>}
                    </div>

                    <div style={{ border: "1px solid #404040", borderRadius: 12, padding: 12, background: "#181818" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ fontWeight: 900 }}>Related Operations</div>
                        <Badge tone="warning">{relatedTargetOperations.length}</Badge>
                      </div>

                      {relatedTargetOperations.length ? relatedTargetOperations.map((item) => (
                        <div key={item?.id || item?.title} style={{ padding: "10px 0", borderTop: "1px solid #303030" }}>
                          <div style={{ fontWeight: 800 }}>{item?.title || "Operation"}</div>
                          <div style={{ color: "#dce6ef", marginTop: 6 }}>{item?.notes || item?.objective || "No notes."}</div>
                          <AttachmentPreviewStrip attachments={item?.attachments} />
                        </div>
                      )) : <div style={{ color: "#9eb0c3" }}>No linked operations.</div>}
                    </div>

                    <div style={{ border: "1px solid #325060", borderRadius: 12, padding: 12, background: "#111920" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                        <div style={{ fontWeight: 900 }}>Timeline Events</div>
                        <Badge tone="normal">{relatedTargetTimeline.length}</Badge>
                      </div>

                      {relatedTargetTimeline.length ? relatedTargetTimeline.map((item) => (
                        <div key={item?.id || item?.title} style={{ padding: "10px 0", borderTop: "1px solid #243a48" }}>
                          <div style={{ fontWeight: 800 }}>{item?.title || "Timeline Event"}</div>
                          <div style={{ color: "#dce6ef", marginTop: 6 }}>{item?.description || "No description."}</div>
                          <AttachmentPreviewStrip attachments={item?.attachments} />
                        </div>
                      )) : <div style={{ color: "#9eb0c3" }}>No linked timeline events.</div>}
                    </div>
                  </div>
                </Card>
              ) : null}


            <div style={{ display: "grid", gap: 12 }}>
              {COUNTY_ORDER.map((county) => {
                const countyTargets = ensureArray(filteredTargets).filter((target) => safeIncludes(getTargetCounties(target), county));
                if (!countyTargets.length) return null;
                return (
                  <Card key={county}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
                      <div style={{ fontWeight: 950, fontSize: 19 }}>{county}</div>
                      <Badge tone="normal">{countyTargets.length} profiles</Badge>
                    </div>

                    <div style={{ display: "grid", gap: 8 }}>
                      {countyTargets.map((target) => {
                        const safeTarget = normalizeTargetProfile(target);
                        const tone = safeTarget.threat_level === "Critical" ? "critical" : safeTarget.threat_level === "High" ? "high" : safeTarget.threat_level === "Low" ? "low" : "normal";
                        const towns = getTargetTowns(safeTarget);
                        const isOpen = selectedTargetId === safeTarget.id;
                        return (
                          <div key={`${county}-${safeTarget.id}`} style={{ border: isOpen ? "1px solid #547da6" : "1px solid #24303c", borderRadius: 14, background: isOpen ? "#172332" : "#10161c", overflow: "hidden" }}>
                            <button
                              type="button"
                              onClick={() => setSelectedTargetId(isOpen ? "" : safeTarget.id)}
                              style={{ width: "100%", textAlign: "left", padding: 12, border: 0, background: "transparent", color: "#fff", cursor: "pointer" }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                                <div>
                                  <div style={{ fontWeight: 950 }}>{safeTarget.name}</div>
                                  <div style={{ color: "#9fb2c4", fontSize: 12, marginTop: 4 }}>
                                    {getTargetCounties(safeTarget).join(", ")} {safeTarget.updated_by_name ? `• Updated by ${safeTarget.updated_by_name}` : ""}
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  <Badge tone={tone}>{safeTarget.threat_level || "Normal"}</Badge>
                                  <span style={{ color: "#9eb0c3", fontWeight: 900 }}>{isOpen ? "−" : "+"}</span>
                                </div>
                              </div>
                            </button>

                            {isOpen ? (
                              <div style={{ borderTop: "1px solid #26313d", padding: 12 }}>
                                {safeTarget.summary ? <div style={{ color: "#dce6ef", fontWeight: 700, marginBottom: 10 }}>{safeTarget.summary}</div> : <div style={{ color: "#9eb0c3", marginBottom: 10 }}>No summary entered.</div>}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                                  {ensureArray(safeTarget.flags).slice(0, 6).map((flag) => <Badge key={flag} tone="warning">{flag}</Badge>)}
                                  {ensureArray(safeTarget.flags).length === 0 ? <Badge tone="default">No flags</Badge> : null}
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, color: "#c8d6e2", fontSize: 13 }}>
                                  <DetailBlock title="Status" items={[safeTarget.status || "Active"]} />
                                  <DetailBlock title="Risk" items={[safeTarget.threat_level || "Normal"]} />
                                  <DetailBlock title="Associated Regions" items={getTargetCounties(safeTarget)} />
                                  <DetailBlock title="Associated Towns / Cities" items={getTargetTowns(safeTarget)} />
                                  <DetailBlock title="Aliases / Monikers" items={safeTarget.aliases} />
                                  <DetailBlock title="Non-CJIS Descriptors" items={safeTarget.identifiers} />
                                  <DetailBlock title="Vehicles" items={safeTarget.vehicles} />
                                  <DetailBlock title="Known / Associated Locations" items={safeTarget.addresses} />
                                  <DetailBlock title="Associates" items={safeTarget.associates} />
                                  <DetailBlock title="Operational Notes" items={safeTarget.notes ? [safeTarget.notes] : []} />
                                </div>

                                <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button type="button" style={secondaryButtonStyle} onClick={() => startEditTargetProfile(safeTarget)}>Edit / Add Information</button>
                                  <button
                                    type="button"
                                    style={secondaryButtonStyle}
                                    onClick={() => {
                                      setIntelForm((prev) => ({
                                        ...prev,
                                        title: `Information Update - ${safeTarget.name}`,
                                        summary: safeTarget.summary || "",
                                        linked_target_ids: Array.from(new Set([...ensureArray(prev?.linked_target_ids), safeTarget.id])),
                                      }));
                                      setNav("create");
                                    }}
                                  >
                                    Create Information From Profile
                                  </button>
                                  <button type="button" style={dangerButtonStyle} onClick={() => deleteLocalTarget(safeTarget.id)}>Delete Local Profile</button>
                                </div>

                                <div style={{ marginTop: 12, padding: 10, borderRadius: 12, background: "#0f151b", border: "1px solid #26313d" }}>
                                  <div style={{ fontWeight: 900, marginBottom: 6 }}>Edit History</div>
                                  {ensureArray(safeTarget.edit_history).length ? (
                                    <div style={{ display: "grid", gap: 5 }}>
                                      {ensureArray(safeTarget.edit_history).slice(0, 5).map((entry, idx) => (
                                        <div key={entry?.id || `${entry?.at || "entry"}-${idx}`} style={{ color: "#9fb2c4", fontSize: 12 }}>
                                          {entry?.action || "Profile edited"} by {entry?.by || "Unknown"} on {formatDateTime(entry?.at)}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div style={{ color: "#9eb0c3", fontSize: 12 }}>
                                      No edit history yet. Future saves will show who edited the profile and when.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}
              {!filteredTargets.length && (
                <Card>
                  <div style={{ color: "#9eb0c3" }}>No subject profiles found.</div>
                </Card>
              )}
            </div>
          </>
        )}


            
        {nav === "operations" && (
          <>
            <SectionTitle
              title="Operations Board"
              subtitle="Lightweight planning board for warrants, surveillance, arrests, and regional task-force operations. Keep CJIS-only records out of the MVP."
              right={<Badge tone="warning">MVP local board</Badge>}
            />

            <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16, alignItems: "start" }}>
              <Card>
                <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 12 }}>Create Operation</div>
                <form onSubmit={submitOperation}>
                  <Label>Operation Title</Label>
                  <Input
                    value={safeOperationForm.title}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), title: e.target.value }))}
                    placeholder="Example: Center narcotics warrant / FAST assist"
                  />

                  <div style={twoColStyle}>
                    <div>
                      <Label>Status</Label>
                      <Select
                        value={safeOperationForm.status}
                        onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), status: e.target.value }))}
                      >
                        {OP_STATUSES.map((x) => <option key={x}>{x}</option>)}
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select
                        value={safeOperationForm.priority}
                        onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), priority: e.target.value }))}
                      >
                        {PRIORITIES.map((x) => <option key={x}>{x}</option>)}
                      </Select>
                    </div>
                  </div>

                  <Label>Objective</Label>
                  <TextArea
                    rows={3}
                    value={safeOperationForm.objective}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), objective: e.target.value }))}
                    placeholder="Operational purpose, target offense pattern, assignment, or mission objective."
                  />

                  <Label>Location / Area</Label>
                  <Input
                    value={safeOperationForm.location}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), location: e.target.value }))}
                    placeholder="Address, neighborhood, county, or general area"
                  />

                  <Label>Date / Time</Label>
                  <Input
                    type="datetime-local"
                    value={safeOperationForm.date}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), date: e.target.value }))}
                  />

                  <Label>Linked Subject Profiles</Label>
                  <div style={chipWrapStyle}>
                    {ensureArray(targets).map((target) => (
                      <Chip
                        key={target?.id || makeId("target-key")}
                        selected={safeIdSelected(safeOperationForm, "linked_target_ids", target)}
                        onClick={() => target?.id && toggleOperationTarget(target.id)}
                      >
                        {target?.name || "Unnamed Subject"}
                      </Chip>
                    ))}
                    {!ensureArray(targets).length && <div style={{ color: "#9eb0c3", fontSize: 13 }}>No target profiles created yet.</div>}
                  </div>

                  <Label>Assigned Personnel / Agencies</Label>
                  <TextArea
                    rows={3}
                    value={safeOperationForm.team}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), team: e.target.value }))}
                    placeholder="One per line. Example: SCSO, RGSO, FAST Entry, perimeter, case agent."
                  />

                  <Label>Staging / Command</Label>
                  <TextArea
                    rows={3}
                    value={safeOperationForm.staging}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), staging: e.target.value }))}
                    placeholder="Staging point, command post, radio notes, routes, or contact plan."
                  />

                  <Label>Equipment / Resources</Label>
                  <TextArea
                    rows={3}
                    value={safeOperationForm.equipment}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), equipment: e.target.value }))}
                    placeholder="One per line. Example: shield, less-lethal, drone, thermal, ram."
                  />

                  <Label>Safety / Tactical Notes</Label>
                  <TextArea
                    rows={3}
                    value={safeOperationForm.safety}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), safety: e.target.value }))}
                    placeholder="Known weapons, dogs, barricade risk, flight risk, medical concerns, children present."
                  />

                  <Label>Additional Notes</Label>
                  <TextArea
                    rows={3}
                    value={safeOperationForm.notes}
                    onChange={(e) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), notes: e.target.value }))}
                  />

                  <AttachmentManager
                    attachments={safeOperationForm.attachments}
                    onChange={(attachments) => setOperationForm((prev) => ({ ...emptyOperationForm, ...(prev || {}), attachments }))}
                    label="Operation Attachments / Photos"
                  />

                  <div style={{ marginTop: 16 }}>
                    <button style={primaryButtonStyle} type="submit">Save Operation</button>
                  </div>
                </form>
              </Card>

              <div>
                {ensureArray(operations).map((op) => (
                  <OperationCard
                    key={op.id}
                    op={op}
                    onStatusChange={(status) => updateOperationStatus(op.id, status)}
                    onDelete={() => deleteLocalOperation(op.id)}
                  />
                ))}
                {!operations.length && (
                  <Card>
                    <div style={{ color: "#9eb0c3" }}>No operations created yet.</div>
                  </Card>
                )}
              </div>
            </div>
          </>
        )}

        
{nav === "map" && (
          <>
            <SectionTitle
              title="Regional Map"
              subtitle="Visual overview of subject profiles, operations, and timeline events. This MVP uses approximate regional pins and local storage until map services/backend routes are added."
              right={<Badge tone="warning">Approximate MVP map</Badge>}
            />
            <Card>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 18 }}>San Luis Valley Regional View</div>
                  <div style={{ color: "#9eb0c3", fontSize: 13 }}>Pins are generated from linked subjects, operations, and timeline entries.</div>
                </div>
                <div style={{ minWidth: 220 }}>
                  <Select value={mapCountyFilter} onChange={(e) => setMapCountyFilter(e.target.value)}>
                    <option>All</option>
                    <option>Saguache</option>
                    <option>Rio Grande</option>
                    <option>Alamosa</option>
                    <option>Conejos</option>
                  </Select>
                </div>
              </div>
              <RegionalMap items={mapItems} />
            </Card>
            <div style={gridStyle}>
              <StatCard label="Visible Pins" value={mapItems.length} />
              <StatCard label="Subject Profiles" value={targets.length} />
              <StatCard label="Operations" value={operations.length} />
              <StatCard label="Timeline Events" value={timelineEvents.length} />
            </div>
            <Card>
              <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 12 }}>Map Pin Details</div>
              {ensureArray(mapItems).map((item) => (
                <MapItemRow key={item?.id || makeId("map-item")} item={item} />
              ))}
              {!mapItems.length && <div style={{ color: "#9eb0c3" }}>No map pins yet. Add a subject profile, operation, or timeline event.</div>}
            </Card>
          </>
        )}

        {nav === "timeline" && (
          <>
            <SectionTitle
              title="Activity Timeline"
              subtitle="Cross-link subject, vehicle, location, observation, and operation events into a searchable activity history."
              right={<Badge tone="target">{filteredTimelineEvents.length} events</Badge>}
            />
            <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 16, alignItems: "start" }}>
              <Card>
                <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 12 }}>Create Timeline Event</div>
                <form onSubmit={submitTimelineEvent}>
                  <Label>Title</Label>
                  <Input value={timelineForm.title} onChange={(e) => setTimelineForm({ ...timelineForm, title: e.target.value })} placeholder="Example: Vehicle observed near Center" />

                  <div style={twoColStyle}>
                    <div>
                      <Label>Event Type</Label>
                      <Select value={timelineForm.event_type} onChange={(e) => setTimelineForm({ ...timelineForm, event_type: e.target.value })}>
                        {EVENT_TYPES.map((x) => <option key={x}>{x}</option>)}
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={timelineForm.priority} onChange={(e) => setTimelineForm({ ...timelineForm, priority: e.target.value })}>
                        {PRIORITIES.map((x) => <option key={x}>{x}</option>)}
                      </Select>
                    </div>
                  </div>

                  <Label>Date / Time</Label>
                  <Input type="datetime-local" value={timelineForm.date} onChange={(e) => setTimelineForm({ ...timelineForm, date: e.target.value })} />

                  <Label>Linked Subject Profile</Label>
                  <Select value={timelineForm.linked_target_id} onChange={(e) => setTimelineForm({ ...timelineForm, linked_target_id: e.target.value })}>
                    <option value="">None</option>
                    {ensureArray(targets).map((target) => <option key={target?.id || makeId("target-key")} value={target?.id || ""}>{target?.name || "Unnamed Subject"}</option>)}
                  </Select>

                  <Label>Vehicle</Label>
                  <Input value={timelineForm.vehicle} onChange={(e) => setTimelineForm({ ...timelineForm, vehicle: e.target.value })} placeholder="Vehicle / plate / color / observed driver" />

                  <Label>Location</Label>
                  <Input value={timelineForm.location} onChange={(e) => setTimelineForm({ ...timelineForm, location: e.target.value })} placeholder="Address, roadway, motel, area, town" />

                  <Label>County</Label>
                  <Select value={timelineForm.county} onChange={(e) => setTimelineForm({ ...timelineForm, county: e.target.value })}>
                    <option value="">Unknown / Regional</option>
                    <option>Saguache</option>
                    <option>Rio Grande</option>
                    <option>Alamosa</option>
                    <option>Conejos</option>
                  </Select>

                  <Label>Description</Label>
                  <TextArea rows={5} value={timelineForm.description} onChange={(e) => setTimelineForm({ ...timelineForm, description: e.target.value })} placeholder="Field-observed or agency-authorized information only. Do not paste CCIC/NCIC/CJIS returns." />

                  <div style={{ marginTop: 16 }}>
                    <button style={primaryButtonStyle} type="submit">Save Timeline Event</button>
                  </div>
                </form>
              </Card>

              <div>
                <Card>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 12 }}>
                    <Input placeholder="Search timeline, vehicle, location, subject..." value={timelineSearch} onChange={(e) => setTimelineSearch(e.target.value)} />
                    <Select value={timelineTargetFilter} onChange={(e) => setTimelineTargetFilter(e.target.value)}>
                      <option value="All">All Subjects</option>
                      {ensureArray(targets).map((target) => <option key={target?.id || makeId("target-key")} value={target?.id || ""}>{target?.name || "Unnamed Subject"}</option>)}
                    </Select>
                  </div>
                </Card>

                <TimelineList
                  events={filteredTimelineEvents}
                  targets={targets}
                  onDelete={deleteTimelineEvent}
                />
              </div>
            </div>
          </>
        )}

        {nav === "regions" && (
          <>
            <SectionTitle
              title="Regions"
              subtitle="Manage operational sharing groups by county, task force, or region."
            />
            <Card>
              <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Existing Regions</div>
              {safeRegions.map((region) => (
                <div
                  key={region?.id}
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid #22303d",
                  }}
                >
                  <div style={{ fontWeight: 800 }}>
                    {region?.name || region?.id || "Region"} <span style={{ color: "#8ea4b8" }}>({region?.id})</span>
                  </div>
                  <div style={{ color: "#b1c1d1", fontSize: 13 }}>{region.description || "No description"}</div>
                  <div style={{ color: "#8ea4b8", fontSize: 12, marginTop: 4 }}>
                    Members: {region.member_count}
                  </div>
                </div>
              ))}
            </Card>

            <Card>
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Create Region</div>
                <form onSubmit={submitRegion}>
                  <Label>Region ID</Label>
                  <Input
                    value={newRegion.id}
                    onChange={(e) => setNewRegion({ ...newRegion, id: e.target.value })}
                    placeholder="Example: NSLV_TASKFORCE"
                  />

                  <Label>Name</Label>
                  <Input
                    value={newRegion.name}
                    onChange={(e) => setNewRegion({ ...newRegion, name: e.target.value })}
                  />

                  <Label>Description</Label>
                  <TextArea
                    rows={4}
                    value={newRegion.description}
                    onChange={(e) => setNewRegion({ ...newRegion, description: e.target.value })}
                  />

                  <div style={{ marginTop: 16 }}>
                    <button style={primaryButtonStyle} type="submit">
                      Create Region
                    </button>
                  </div>
                </form>
              </Card>
          </>
        )}

        {nav === "messages" && (
          <>
            <SectionTitle
              title="Messages"
              subtitle="Direct user-to-user communication."
            />

            <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16 }}>
              <Card>
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 12 }}>Users</div>
                {ensureArray(users).map((user) => (
                  <button
                    key={user.id}
                    onClick={() => openConversation(user)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: 12,
                      marginBottom: 8,
                      borderRadius: 12,
                      border: "1px solid #24303c",
                      background:
                        selectedChatUser?.id === user.id ? "#223142" : "#11171d",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{user.display_name}</div>
                    <div style={{ fontSize: 12, color: "#9fb2c4" }}>
                      {user.agency_short_name || user.agency_id || ""}
                    </div>
                  </button>
                ))}
              </Card>

              <Card>
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 12 }}>
                  {selectedChatUser
                    ? `Conversation with ${selectedChatUser.display_name}`
                    : "Select a user"}
                </div>

                <div
                  style={{
                    minHeight: 300,
                    maxHeight: 500,
                    overflowY: "auto",
                    border: "1px solid #24303c",
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 14,
                    background: "#11171d",
                  }}
                >
                  {ensureArray(messages).map((m) => {
                    const mine = m.sender_id === me?.id;
                    return (
                      <div
                        key={m.id}
                        style={{
                          display: "flex",
                          justifyContent: mine ? "flex-end" : "flex-start",
                          marginBottom: 10,
                        }}
                      >
                        <div
                          style={{
                            maxWidth: "70%",
                            padding: 10,
                            borderRadius: 12,
                            background: mine ? "#24405e" : "#24303c",
                            color: "#fff",
                          }}
                        >
                          <div style={{ fontSize: 13 }}>{m.body}</div>
                          <div style={{ fontSize: 11, color: "#bdd0e2", marginTop: 4 }}>
                            {new Date(m.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {!ensureArray(messages).length && (
                    <div style={{ color: "#9eb0c3" }}>
                      {selectedChatUser ? "No messages yet." : "Choose a user to begin."}
                    </div>
                  )}
                </div>

                {selectedChatUser && (
                  <form onSubmit={submitMessage}>
                    <TextArea
                      rows={4}
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      placeholder="Type message..."
                    />
                    <div style={{ marginTop: 12 }}>
                      <button style={primaryButtonStyle} type="submit">
                        Send Message
                      </button>
                    </div>
                  </form>
                )}
              </Card>
            </div>
          </>
        )}

        {nav === "admin" && (
          <>
            <SectionTitle
              title="Admin"
              subtitle="Full administrative controls: roles, region access, subscriptions, regions, permissions, and audit visibility."
              right={<Badge tone="critical">Admin</Badge>}
            />

            <Card accent>
              <div style={{ fontWeight: 900, fontSize: 20, marginBottom: 8 }}>Admin Profile</div>
              <div style={{ color: "#9eb0c3", fontSize: 13, marginBottom: 14 }}>
                This profile is the permanent local admin fallback until backend accounts are connected.
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={adminProfile?.display_name || ""}
                    onChange={(e) => updateAdminProfileField("display_name", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Agency</Label>
                  <Select
                    value={adminProfile?.agency_id || "SCSO"}
                    onChange={(e) => updateAdminProfileField("agency_id", e.target.value)}
                  >
                    {safeAgencies.map((agency) => (
                      <option key={agency?.id} value={agency?.id}>
                        {agency?.short_name || agency?.name || agency?.id}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <Label>Admin Authorized Regions / Subscriptions</Label>
                <div style={chipWrapStyle}>
                  {["SLV", ...DEFAULT_ALERT_REGIONS].map((regionName) => (
                    <Chip
                      key={`admin-${regionName}`}
                      selected={safeIncludes(getUserAssignedRegions(adminProfile), regionName)}
                      onClick={() => updateAdminAssignedRegion(regionName)}
                    >
                      {regionName === "SLV" ? "SLV — San Luis Valley" : regionName}
                    </Chip>
                  ))}
                </div>
              </div>
            </Card>

            <Card>
              <div style={{ fontWeight: 900, fontSize: 20, marginBottom: 8 }}>Create / Assign User</div>
              <form onSubmit={createAdminManagedUser}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                  <div>
                    <Label>Display Name</Label>
                    <Input
                      value={adminUserDraft.display_name}
                      onChange={(e) => setAdminUserDraft((prev) => ({ ...(prev || {}), display_name: e.target.value }))}
                      placeholder="Deputy / Investigator Name"
                    />
                  </div>
                  <div>
                    <Label>Email / Username</Label>
                    <Input
                      value={adminUserDraft.email}
                      onChange={(e) => setAdminUserDraft((prev) => ({ ...(prev || {}), email: e.target.value }))}
                      placeholder="user@agency.gov"
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select
                      value={adminUserDraft.role}
                      onChange={(e) => setAdminUserDraft((prev) => ({ ...(prev || {}), role: e.target.value }))}
                    >
                      <option value="Patrol">Patrol</option>
                      <option value="Investigations">Investigations</option>
                      <option value="Admin">Admin</option>
                    </Select>
                  </div>
                  <div>
                    <Label>Agency</Label>
                    <Select
                      value={adminUserDraft.agency_id}
                      onChange={(e) => setAdminUserDraft((prev) => ({ ...(prev || {}), agency_id: e.target.value }))}
                    >
                      {safeAgencies.map((agency) => (
                        <option key={agency?.id} value={agency?.id}>
                          {agency?.short_name || agency?.name || agency?.id}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <Label>Assigned Regions / Subscriptions</Label>
                  <div style={chipWrapStyle}>
                    {["SLV", ...DEFAULT_ALERT_REGIONS].map((regionName) => (
                      <Chip
                        key={`draft-${regionName}`}
                        selected={safeIncludes(adminUserDraft.assigned_regions, regionName)}
                        onClick={() => setAdminUserDraft((prev) => {
                          const current = ensureArray(prev?.assigned_regions);
                          const exists = current.includes(regionName);
                          const next = exists ? current.filter((x) => x !== regionName) : [...current, regionName];
                          return { ...(prev || {}), assigned_regions: next };
                        })}
                      >
                        {regionName === "SLV" ? "SLV — San Luis Valley" : regionName}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <button type="submit" style={primaryButtonStyle}>Create User</button>
                </div>
              </form>
            </Card>

            <Card>
              <div style={{ fontWeight: 900, fontSize: 20, marginBottom: 8 }}>Managed Users</div>
              {ensureArray(users).length ? (
                <div style={{ display: "grid", gap: 10 }}>
                  {ensureArray(users).map((user, index) => {
                    const userId = user?.id || user?.email || `user-${index}`;
                    const assigned = getUserAssignedRegions(user);
                    return (
                      <div key={userId} style={{ border: "1px solid #26313d", borderRadius: 12, padding: 12, background: "#11171d" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 900 }}>{user?.display_name || user?.displayName || user?.username || user?.email || userId}</div>
                            <div style={{ color: "#9eb0c3", fontSize: 12 }}>{user?.email || "No email"} • {user?.agency_id || user?.agency || "No agency"}</div>
                          </div>
                          <Select
                            value={getUserRoleLabel(user)}
                            onChange={(e) => updateManagedUserRole(userId, e.target.value)}
                          >
                            <option value="Patrol">Patrol</option>
                            <option value="Investigations">Investigations</option>
                            <option value="Admin">Admin</option>
                          </Select>
                        </div>
                        <div style={{ ...chipWrapStyle, marginTop: 10 }}>
                          {["SLV", ...DEFAULT_ALERT_REGIONS].map((regionName) => (
                            <Chip
                              key={`${userId}-${regionName}`}
                              selected={assigned.includes(regionName)}
                              onClick={() => toggleManagedUserRegion(userId, regionName)}
                            >
                              {regionName === "SLV" ? "SLV — San Luis Valley" : regionName}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#9eb0c3" }}>No managed users loaded yet.</div>
              )}
            </Card>

            <Card>
              <div style={{ fontWeight: 900, fontSize: 20, marginBottom: 8 }}>Region Management</div>
              <div style={{ color: "#9eb0c3", fontSize: 13, marginBottom: 12 }}>
                Admins can create regions under the Regions page. SLV functions as a mega-region covering the San Luis Valley sub-regions.
              </div>
              <button type="button" style={secondaryButtonStyle} onClick={() => setNav("regions")}>
                Open Region Management
              </button>
            </Card>

            <Card>
              <div style={{ fontWeight: 900, fontSize: 20, marginBottom: 8 }}>Audit Trail</div>
              <div style={{ color: "#9eb0c3", fontSize: 13, marginBottom: 12 }}>
                Admins can view audit activity and clear the local audit log during MVP testing.
              </div>
              <button type="button" style={secondaryButtonStyle} onClick={() => setNav("audit")}>
                Open Audit Trail
              </button>
            </Card>
          </>
        )}

        {nav === "audit" && (
          <>
            <SectionTitle
              title="Audit Log"
              subtitle="Local activity tracking for the MVP. Backend audit logging should be added before production deployment."
              right={<button type="button" style={smallDangerButtonStyle} onClick={clearAuditLog}>Clear Local Log</button>}
            />

            <Card>
              {ensureArray(auditLogs).map((entry) => (
                <div key={entry.id} style={{ padding: 12, border: "1px solid #26313d", borderRadius: 12, marginBottom: 10, background: "#11171d" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ fontWeight: 900 }}>{entry.action}</div>
                    <div style={{ color: "#9eb0c3", fontSize: 12 }}>{safeDate(entry.created_at)}</div>
                  </div>
                  <div style={{ color: "#b8c7d6", fontSize: 13, marginTop: 4 }}>{entry.details}</div>
                  <div style={{ color: "#7f93a8", fontSize: 12, marginTop: 4 }}>{entry.user} • {entry.agency}</div>
                </div>
              ))}
              {!auditLogs.length && <div style={{ color: "#9eb0c3" }}>No local audit events yet.</div>}
            </Card>
          </>
        )}

      </div>
    </div>
  );
}

function Alert({ children, tone }) {
  const error = tone === "error";
  return (
    <div
      style={{
        background: error ? "#5a1f1f" : "#173a2a",
        border: error ? "1px solid #7a2e2e" : "1px solid #2c684f",
        color: "#fff",
        padding: 12,
        borderRadius: 12,
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

function IntelCard({ item }) {
  const tone =
    item.priority === "Critical"
      ? "critical"
      : item.priority === "High"
      ? "high"
      : item.priority === "Low"
      ? "low"
      : "normal";

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{item.title}</div>
          <div style={{ color: "#9db0c2", fontSize: 13, marginTop: 4 }}>
            {item.post_type} • {item.created_by_name || "Unknown"}{" "}
            {item.created_by_agency ? `(${item.created_by_agency})` : ""}
          </div>
        </div>
        <Badge tone={tone}>{item.priority}</Badge>
      </div>

      {item.summary ? (
        <div style={{ marginTop: 10, fontWeight: 700, color: "#dbe5ee" }}>{item.summary}</div>
      ) : null}

      <div style={{ marginTop: 10, color: "#d0dbe6", whiteSpace: "pre-wrap" }}>{item.body}</div>

      <div style={{ marginTop: 12, color: "#89a0b4", fontSize: 12 }}>
        Created: {safeDate(item.created_at)}
        {item.expires_at ? ` • Expires: ${safeDate(item.expires_at)}` : ""}
      </div>

      {Array.isArray(item.targets) && item.targets.length > 0 ? (
        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {ensureArray(item.targets).map((t, idx) => (
            <Badge key={`${t.target_type}-${t.target_id}-${idx}`}>
              {String(t.target_type || "target").toUpperCase()}: {t.target_id}
            </Badge>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function TargetMiniCard({ target, onOpen }) {
  return (
    <button type="button" onClick={onOpen} style={targetMiniButtonStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 900 }}>{target?.name || "Unnamed Subject"}</div>
        <Badge tone="critical">{target.threat_level}</Badge>
      </div>
      <div style={{ color: "#b8c7d6", fontSize: 13, marginTop: 8 }}>{target.summary || "No summary."}</div>
      <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {ensureArray(target.flags).slice(0, 3).map((flag) => <Badge key={flag} tone="warning">{flag}</Badge>)}
      </div>
    </button>
  );
}

function TargetDetail({ target, relatedIntel, relatedTimeline = [], onCreateIntel, onDelete }) {
  const tone =
    target.threat_level === "Critical"
      ? "critical"
      : target.threat_level === "High"
      ? "high"
      : target.threat_level === "Low"
      ? "low"
      : "normal";

  return (
    <>
      <Card accent>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 950 }}>{target?.name || "Unnamed Subject"}</div>
            <div style={{ color: "#9fb2c4", fontSize: 13, marginTop: 4 }}>
              {target.status || "Active"} • Created by {target.created_by_name || "Unknown"}
              {target.created_by_agency ? ` (${target.created_by_agency})` : ""}
            </div>
          </div>
          <Badge tone={tone}>{target.threat_level}</Badge>
        </div>

        {target.summary ? <div style={{ marginTop: 14, color: "#dce6ef", fontWeight: 700 }}>{target.summary}</div> : null}

        {ensureArray(target.flags).length ? (
          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {ensureArray(target.flags).map((flag) => <Badge key={flag} tone="warning">{flag}</Badge>)}
          </div>
        ) : null}

        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" style={primaryButtonStyle} onClick={onCreateIntel}>Create Information From Profile</button>
          <button type="button" style={dangerButtonStyle} onClick={onDelete}>Delete Local Profile</button>
        </div>
      </Card>

      <div style={twoColStyle}>
        <InfoList title="Aliases" items={target.aliases} />
        <InfoList title="Associated Counties" items={getTargetCounties(target)} />
      </div>

      <div style={twoColStyle}>
        <InfoList title="Associated Towns / Cities" items={getTargetTowns(target)} />
        <InfoList title="Non-CJIS Descriptors" items={target.identifiers} />
      </div>

      <div style={twoColStyle}>
        <InfoList title="Vehicles" items={target.vehicles} />
        <InfoList title="Locations" items={target.addresses} />
      </div>

      <div style={twoColStyle}>
        <InfoList title="Associates" items={target.associates} />
        <InfoList title="Operational Notes" items={target.notes ? [target.notes] : []} multiline />
      </div>

      <Card>
        <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>Related Information</div>
        {relatedIntel.slice(0, 5).map((item) => <IntelCard key={item?.id || makeId("map-item")} item={item} />)}
        {!ensureArray(relatedIntel).length && <div style={{ color: "#9eb0c3" }}>No related information found by profile name yet.</div>}
      </Card>
    </>
  );
}

function DetailBlock({ title, items }) {
  const list = ensureArray(items);
  return (
    <div style={{ border: "1px solid #26313d", borderRadius: 12, padding: 10, background: "#0f151b" }}>
      <div style={{ fontWeight: 900, color: "#e8f0f7", marginBottom: 6 }}>{title}</div>
      {list.length ? (
        <div style={{ display: "grid", gap: 4 }}>
          {list.map((item, idx) => (
            <div key={`${title}-${idx}`} style={{ color: "#b8c7d6", whiteSpace: "pre-wrap" }}>{textValue(item)}</div>
          ))}
        </div>
      ) : (
        <div style={{ color: "#73879a" }}>None entered.</div>
      )}
    </div>
  );
}

function InfoList({ title, items, multiline = false }) {
  return (
    <Card>
      <div style={{ fontWeight: 900, fontSize: 17, marginBottom: 10 }}>{title}</div>
      {Array.isArray(items) && items.length ? (
        <div style={{ display: "grid", gap: 8 }}>
          {ensureArray(items).map((item, idx) => (
            <div key={`${title}-${idx}`} style={{ color: "#d5e0ea", whiteSpace: multiline ? "pre-wrap" : "normal" }}>
              {item}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: "#9eb0c3" }}>None entered.</div>
      )}
    </Card>
  );
}



function RegionAlertDashboard({ alerts, alertSubscriptions, alertRegionOptions, onToggleSubscription, onOpenFeed }) {
  const topAlerts = ensureArray(alerts).slice(0, 5);

  return (
    <Card accent={ensureArray(alerts).length > 0}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: 950, fontSize: 20 }}>Regional Alert Subscriptions</div>
          <div style={{ color: "#9eb0c3", fontSize: 13, marginTop: 4 }}>
            Select the counties/regions that should trigger dashboard alerts. Every information post for subscribed areas appears here regardless of risk level. You can still access all information from the Regional Feed and map.
          </div>
        </div>
        <Badge tone={ensureArray(alerts).length ? "critical" : "normal"}>{ensureArray(alerts).length} subscribed regional notices</Badge>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
        {ensureArray(alertRegionOptions).map((region) => (
          <Chip key={region} selected={safeIncludes(alertSubscriptions, region)} onClick={() => onToggleSubscription(region)}>
            {safeIncludes(alertSubscriptions, region) ? "✓ " : ""}{region}
          </Chip>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {topAlerts.length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {ensureArray(topAlerts).map((item) => (
              <button key={item?.id || makeId("alert")} type="button" onClick={onOpenFeed} style={alertRowButtonStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                  <div style={{ fontWeight: 900 }}>{item?.title || "Regional Notice"}</div>
                  <Badge tone={item?.priority === "Critical" ? "critical" : item?.priority === "High" ? "high" : "normal"}>{item?.priority || "Alert"}</Badge>
                </div>
                <div style={{ color: "#9eb0c3", fontSize: 12, marginTop: 4 }}>
                  {item?.post_type || "Regional Alert"} • {ensureArray(item?.alert_regions).length ? ensureArray(item?.alert_regions).join(", ") : "Region not specified"} • {safeDate(item?.created_at)}
                </div>
                {item?.summary ? <div style={{ color: "#d5e0ea", fontSize: 13, marginTop: 6 }}>{item.summary}</div> : null}
              </button>
            ))}
          </div>
        ) : (
          <div style={{ color: "#9eb0c3", marginTop: 10 }}>
            No current information items for your subscribed regions.
          </div>
        )}
      </div>
    </Card>
  );
}

function RegionalMap({ items }) {
  const [selectedPin, setSelectedPin] = useState(null);

  function pinTone(priority) {
    if (priority === "Critical") return { border: "#ff7070", glow: "rgba(255,112,112,.24)", label: "Critical risk" };
    if (priority === "High") return { border: "#ffbd66", glow: "rgba(255,189,102,.2)", label: "High risk" };
    if (priority === "Low") return { border: "#72d48a", glow: "rgba(114,212,138,.16)", label: "Low risk" };
    return { border: "#78a6d6", glow: "rgba(120,166,214,.16)", label: "Normal risk" };
  }

  return (
    <div style={mapCanvasStyle}>
      <div style={mapGridLabelStyle}>SAN LUIS VALLEY / REGIONAL VIEW</div>
      <div style={mapLegendStyle}>
        <span style={mapLegendItemStyle}><span style={mapTownDotStyle} /> Community</span>
        <span style={mapLegendItemStyle}><span style={{ ...mapLegendDotStyle, borderColor: "#72d48a" }} /> Low</span>
        <span style={mapLegendItemStyle}><span style={{ ...mapLegendDotStyle, borderColor: "#78a6d6" }} /> Normal</span>
        <span style={mapLegendItemStyle}><span style={{ ...mapLegendDotStyle, borderColor: "#ffbd66" }} /> High</span>
        <span style={mapLegendItemStyle}><span style={{ ...mapLegendDotStyle, borderColor: "#ff7070" }} /> Critical</span>
      </div>
      <div style={{ ...mapRegionStyle, left: "10%", top: "14%", width: "70%", height: "46%" }}>Saguache County</div>
      <div style={{ ...mapRegionStyle, left: "12%", top: "60%", width: "40%", height: "28%" }}>Rio Grande County</div>
      <div style={{ ...mapRegionStyle, left: "54%", top: "56%", width: "36%", height: "30%" }}>Alamosa County</div>
      <div style={{ ...mapRegionStyle, left: "43%", top: "84%", width: "42%", height: "12%" }}>Conejos County</div>

      {MAP_LOCATIONS.map((loc) => (
        <div key={loc.id} style={{ ...mapTownStyle, left: `${loc.x}%`, top: `${loc.y}%` }}>
          <span style={mapTownDotStyle} /> {loc.name}
        </div>
      ))}

      {ensureArray(items).map((item) => {
        const tone = pinTone(item?.priority);
        const selected = selectedPin?.id === item?.id;
        return (
          <button
            key={item?.id || makeId("map-item")}
            type="button"
            title={`${item?.type || "Pin"}: ${item?.label || "Map item"}`}
            onClick={() => setSelectedPin(selected ? null : item)}
            style={{
              ...mapPinStyle,
              left: `${Math.max(5, Math.min(94, Number(item?.x || 50)))}%`,
              top: `${Math.max(8, Math.min(92, Number(item?.y || 50)))}%`,
              borderColor: tone.border,
              boxShadow: selected ? `0 0 0 4px ${tone.glow}, 0 12px 26px rgba(0,0,0,.45)` : `0 0 0 2px ${tone.glow}, 0 8px 18px rgba(0,0,0,.34)`,
              transform: selected ? "translate(-50%, -50%) scale(1.14)" : "translate(-50%, -50%)",
            }}
          >
            {(item?.priority === "Critical" || item?.priority === "High") ? <div style={{ ...mapPulseStyle, borderColor: tone.border }} /> : null}
            <span style={mapPinCoreStyle} />
          </button>
        );
      })}

      {selectedPin ? (
        <div
          style={{
            ...mapPopupStyle,
            left: `${Math.max(24, Math.min(76, selectedPin.x))}%`,
            top: `${selectedPin.y > 62 ? Math.max(12, selectedPin.y - 28) : Math.min(76, selectedPin.y + 8)}%`,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 10 }}>
            <div>
              <div style={{ color: "#9eb0c3", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: .7 }}>
                {selectedPin.type} • {selectedPin.county} County
              </div>
              <div style={{ fontWeight: 950, fontSize: 16, marginTop: 3 }}>{selectedPin.label}</div>
            </div>
            <button type="button" onClick={() => setSelectedPin(null)} style={mapPopupCloseStyle}>×</button>
          </div>
          <div style={{ marginTop: 8 }}>
            <Badge tone={selectedPin.priority === "Critical" ? "critical" : selectedPin.priority === "High" ? "high" : selectedPin.priority === "Low" ? "low" : "normal"}>
              {selectedPin.priority || "Normal"} Risk
            </Badge>
          </div>
          <div style={{ color: "#d5e0ea", fontSize: 13, marginTop: 10, lineHeight: 1.35 }}>
            {selectedPin.detail || "No additional detail entered."}
          </div>
          <div style={{ color: "#8faac2", fontSize: 11, marginTop: 10 }}>
            Pin ID: {selectedPin.id}
          </div>
        </div>
      ) : (
        <div style={mapHintStyle}>Click a small risk pin to view details.</div>
      )}
    </div>
  );
}

function MapItemRow({ item }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #22303d", display: "grid", gridTemplateColumns: "120px 1fr 110px", gap: 10 }}>
      <div><Badge tone={item.priority === "Critical" ? "critical" : item.priority === "High" ? "high" : "normal"}>{item.type}</Badge></div>
      <div>
        <div style={{ fontWeight: 900 }}>{item.label}</div>
        <div style={{ color: "#9eb0c3", fontSize: 13 }}>{item.detail}</div>
      </div>
      <div style={{ color: "#b6c6d6", fontSize: 13, textAlign: "right" }}>{item.county}</div>
    </div>
  );
}

function TimelineList({ events, targets, onDelete }) {
  if (!ensureArray(events).length) {
    return <Card><div style={{ color: "#9eb0c3" }}>No timeline events match your filters.</div></Card>;
  }

  return (
    <Card>
      {ensureArray(events).map((event) => {
        const target = ensureArray(targets).find((x) => x?.id === event?.linked_target_id);
        return (
          <div key={event?.id || makeId("timeline-row")} style={timelineItemStyle}>
            <div style={timelineDotStyle} />
            <div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ fontWeight: 900, fontSize: 16 }}>{event?.title || "Timeline Event"}</div>
                <Badge tone={event?.priority === "Critical" ? "critical" : event?.priority === "High" ? "high" : "normal"}>{event?.priority || "Normal"}</Badge>
                <Badge tone="target">{event?.event_type || "Event"}</Badge>
              </div>
              <div style={{ color: "#9eb0c3", fontSize: 12, marginTop: 4 }}>
                {event?.date ? new Date(event.date).toLocaleString() : "No date entered"}
                {event?.location ? ` • ${event.location}` : ""}
                {event?.county ? ` • ${event.county} County` : ""}
              </div>
              {target ? <div style={{ color: "#d4e4f4", fontSize: 13, marginTop: 6 }}>Linked Subject: <b>{target?.name || "Unnamed Subject"}</b></div> : null}
              {event?.vehicle ? <div style={{ color: "#d4e4f4", fontSize: 13, marginTop: 4 }}>Vehicle: {event.vehicle}</div> : null}
              {event?.description ? <div style={{ color: "#dce6ef", fontSize: 14, marginTop: 8, whiteSpace: "pre-wrap" }}>{event.description}</div> : null}
              <div style={{ marginTop: 8 }}>
                <button type="button" onClick={() => event?.id && onDelete(event.id)} style={smallDangerButtonStyle}>Delete local event</button>
              </div>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

function TimelineMiniRow({ event }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #22303d" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontWeight: 900 }}>{event?.title || "Timeline Event"}</div>
        <Badge tone={event?.priority === "Critical" ? "critical" : event?.priority === "High" ? "high" : "normal"}>{event?.event_type || "Event"}</Badge>
      </div>
      <div style={{ color: "#9eb0c3", fontSize: 12, marginTop: 4 }}>
        {event.date ? new Date(event.date).toLocaleString() : "No date"}{event?.location ? ` • ${event.location}` : ""}
      </div>
    </div>
  );
}

function OperationCard({ op, onStatusChange, onDelete }) {
  const safeOp = op || {};
  const priorityTone = safeOp.priority === "Critical" ? "critical" : safeOp.priority === "High" ? "high" : "normal";
  return (
    <Card accent={safeOp.status === "Active"}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 900 }}>{safeOp.title || "Untitled Operation"}</div>
          <div style={{ color: "#8fa3b7", fontSize: 12, marginTop: 4 }}>
            {safeOp.location || "No location listed"} • {safeOp.date ? safeDate(safeOp.date) : "No date set"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Badge tone={priorityTone}>{safeOp.priority || "Normal"}</Badge>
          <Badge tone={safeOp.status === "Active" ? "critical" : "default"}>{safeOp.status || "Planning"}</Badge>
        </div>
      </div>

      {safeOp.objective ? <div style={{ marginTop: 12, color: "#d5e0ea" }}>{safeOp.objective}</div> : null}

      {ensureArray(safeOp.linked_targets).length ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900, marginBottom: 6 }}>Linked Targets</div>
          <div style={chipWrapStyle}>
            {ensureArray(safeOp.linked_targets).map((target) => (
              <Badge key={target?.id || makeId("target-key")} tone={target?.threat_level === "Critical" ? "critical" : "target"}>
                {target?.name || "Unnamed Subject"}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginTop: 14 }}>
        <InfoBlock title="Team / Agencies" items={safeOp.team} fallback="No personnel listed." />
        <InfoBlock title="Equipment" items={safeOp.equipment} fallback="No equipment listed." />
        <InfoBlock title="Safety Notes" items={safeOp.safety} fallback="No safety notes listed." />
      </div>

      {safeOp.staging ? <FieldBlock title="Staging / Command" value={safeOp.staging} /> : null}
      {safeOp.notes ? <FieldBlock title="Additional Notes" value={safeOp.notes} /> : null}

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <Select value={safeOp.status || "Planning"} onChange={(e) => onStatusChange(e.target.value)}>
          {OP_STATUSES.map((x) => <option key={x}>{x}</option>)}
        </Select>
        <button type="button" style={dangerButtonStyle} onClick={onDelete}>Delete Local Copy</button>
      </div>
    </Card>
  );
}

function InfoBlock({ title, items, fallback }) {
  const list = Array.isArray(items) ? items : splitLines(items);
  return (
    <div style={{ border: "1px solid #26313d", borderRadius: 12, padding: 12, background: "#10161c" }}>
      <div style={{ fontWeight: 900, marginBottom: 6 }}>{title}</div>
      {list.length ? (
        <ul style={{ margin: 0, paddingLeft: 18, color: "#c9d7e4" }}>
          {ensureArray(list).map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
        </ul>
      ) : (
        <div style={{ color: "#8fa3b7", fontSize: 13 }}>{fallback}</div>
      )}
    </div>
  );
}

function FieldBlock({ title, value }) {
  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #26313d", paddingTop: 12 }}>
      <div style={{ fontWeight: 900, marginBottom: 4 }}>{title}</div>
      <div style={{ whiteSpace: "pre-wrap", color: "#cbd8e4" }}>{value}</div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div
      style={{
        background: "#151a20",
        border: "1px solid #26313d",
        borderRadius: 14,
        padding: 18,
      }}
    >
      <div style={{ fontSize: 13, color: "#96aabd" }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 900, marginTop: 8 }}>{value}</div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 13, fontWeight: 800, marginTop: 14, marginBottom: 6 }}>{children}</div>;
}

function Input(props) {
  const safeProps = { ...props, value: props.value === null || props.value === undefined ? "" : String(props.value) };
  return (
    <input
      {...safeProps}
      style={{
        width: "100%",
        background: "#0f1419",
        color: "#fff",
        border: "1px solid #2a3642",
        borderRadius: 10,
        padding: 12,
        boxSizing: "border-box",
      }}
    />
  );
}

function Select(props) {
  const safeProps = { ...props, value: props.value === null || props.value === undefined ? "" : String(props.value) };
  return (
    <select
      {...safeProps}
      style={{
        width: "100%",
        background: "#0f1419",
        color: "#fff",
        border: "1px solid #2a3642",
        borderRadius: 10,
        padding: 12,
        boxSizing: "border-box",
      }}
    />
  );
}

function TextArea(props) {
  const safeProps = { ...props, value: props.value === null || props.value === undefined ? "" : String(props.value) };
  return (
    <textarea
      {...safeProps}
      style={{
        width: "100%",
        background: "#0f1419",
        color: "#fff",
        border: "1px solid #2a3642",
        borderRadius: 10,
        padding: 12,
        boxSizing: "border-box",
        resize: "vertical",
      }}
    />
  );
}

function Chip({ children, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: selected ? "1px solid #6b95bf" : "1px solid #31404f",
        background: selected ? "#27415d" : "#10161b",
        color: "#fff",
        borderRadius: 999,
        padding: "8px 12px",
        cursor: "pointer",
        fontWeight: 700,
      }}
    >
      {children}
    </button>
  );
}


const smallButtonStyle = {
  padding: "7px 10px",
  borderRadius: 10,
  border: "1px solid #35506b",
  background: "#1d3146",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 800,
  fontSize: 12,
};

const smallDangerButtonStyle = {
  ...smallButtonStyle,
  border: "1px solid #633",
  background: "#472020",
};

function safeDate(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
}

const disclosureOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "grid",
  placeItems: "center",
  padding: 18,
  background: "rgba(0,0,0,0.78)",
  backdropFilter: "blur(8px)",
};

const disclosureModalStyle = {
  width: "min(760px, 100%)",
  maxHeight: "88vh",
  overflow: "auto",
  background: "linear-gradient(135deg, #111820, #151d28)",
  border: "1px solid #3a4e63",
  borderRadius: 18,
  padding: 22,
  boxShadow: "0 30px 80px rgba(0,0,0,.45)",
};

const mapCanvasStyle = {
  position: "relative",
  height: 520,
  border: "1px solid #2b3d4f",
  borderRadius: 16,
  overflow: "hidden",
  background: "radial-gradient(circle at 50% 42%, #1d2a36, #0c1117 72%)",
};

const mapGridLabelStyle = {
  position: "absolute",
  left: 16,
  top: 14,
  color: "#8faac2",
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: 1,
};

const mapLegendStyle = {
  position: "absolute",
  right: 14,
  top: 12,
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  zIndex: 6,
};

const mapLegendItemStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 8px",
  borderRadius: 999,
  background: "rgba(8,12,16,.74)",
  border: "1px solid rgba(255,255,255,.1)",
  color: "#c9d8e5",
  fontSize: 11,
  fontWeight: 800,
};

const mapLegendDotStyle = {
  width: 9,
  height: 9,
  borderRadius: 999,
  border: "2px solid #78a6d6",
  background: "rgba(10,15,20,.88)",
};

const mapCorridorStyle = {
  position: "absolute",
  height: 22,
  borderTop: "1px solid rgba(135,170,202,.22)",
  borderBottom: "1px solid rgba(135,170,202,.12)",
  color: "rgba(196,216,236,.34)",
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: .5,
  textAlign: "center",
  zIndex: 1,
};

const mapRegionStyle = {
  position: "absolute",
  border: "1px solid rgba(137,171,203,.24)",
  borderRadius: 22,
  background: "rgba(92,124,154,.08)",
  color: "rgba(207,225,242,.28)",
  fontSize: 18,
  fontWeight: 900,
  display: "grid",
  placeItems: "center",
};

const mapTownStyle = {
  position: "absolute",
  transform: "translate(-50%, -50%)",
  color: "#bbccdc",
  fontSize: 12,
  background: "rgba(8,12,16,.72)",
  border: "1px solid rgba(255,255,255,.1)",
  borderRadius: 999,
  padding: "3px 8px",
  zIndex: 2,
};

const mapTownDotStyle = {
  display: "inline-block",
  width: 6,
  height: 6,
  borderRadius: 999,
  background: "#90a9c1",
};

const mapPulseStyle = {
  position: "absolute",
  inset: -5,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.2)",
  boxShadow: "0 0 0 5px rgba(255,189,102,.06)",
  zIndex: 1,
};

const mapPinStyle = {
  position: "absolute",
  transform: "translate(-50%, -50%)",
  width: 16,
  height: 16,
  minWidth: 16,
  padding: 0,
  borderRadius: 999,
  border: "2px solid #78a6d6",
  background: "rgba(10,15,20,.92)",
  color: "#fff",
  display: "grid",
  placeItems: "center",
  zIndex: 5,
  overflow: "visible",
  cursor: "pointer",
};

const mapPinCoreStyle = {
  width: 6,
  height: 6,
  borderRadius: 999,
  background: "#fff",
  opacity: .95,
  position: "relative",
  zIndex: 2,
};

const mapPopupStyle = {
  position: "absolute",
  transform: "translate(-50%, 0)",
  width: "min(330px, 86%)",
  padding: 14,
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,.16)",
  background: "rgba(8,12,16,.96)",
  boxShadow: "0 18px 40px rgba(0,0,0,.55)",
  color: "#ecf3f9",
  zIndex: 12,
};

const mapPopupCloseStyle = {
  border: "1px solid rgba(255,255,255,.14)",
  background: "rgba(255,255,255,.06)",
  color: "#d8e4ef",
  width: 28,
  height: 28,
  borderRadius: 999,
  cursor: "pointer",
  fontSize: 18,
  lineHeight: 1,
};

const mapHintStyle = {
  position: "absolute",
  left: 14,
  bottom: 12,
  color: "#9eb0c3",
  fontSize: 12,
  background: "rgba(8,12,16,.72)",
  border: "1px solid rgba(255,255,255,.1)",
  borderRadius: 999,
  padding: "5px 10px",
  zIndex: 8,
};

const timelineItemStyle = {
  position: "relative",
  padding: "0 0 18px 28px",
  borderLeft: "2px solid #2d4052",
  marginLeft: 8,
  marginBottom: 4,
};

const timelineDotStyle = {
  position: "absolute",
  left: -8,
  top: 2,
  width: 14,
  height: 14,
  borderRadius: 999,
  background: "#78a6d6",
  border: "2px solid #0d1217",
};



const alertRowButtonStyle = {
  width: "100%",
  textAlign: "left",
  border: "1px solid #334558",
  borderRadius: 12,
  padding: 12,
  background: "#111922",
  color: "#ecf3f9",
  cursor: "pointer",
};

const shellStyle = {
  minHeight: "100vh",
  background: "#0b0f13",
  color: "#f3f7fb",
  display: "grid",
  gridTemplateColumns: "280px 1fr",
};

const sidebarStyle = {
  borderRight: "1px solid #1d2731",
  padding: 18,
  background: "#0e1318",
};

const mainStyle = {
  padding: 22,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 14,
  marginBottom: 18,
};

const targetGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 12,
};

const twoColStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 14,
};

const chipWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const primaryButtonStyle = {
  background: "#315478",
  color: "#fff",
  border: "1px solid #41688f",
  borderRadius: 10,
  padding: "12px 16px",
  cursor: "pointer",
  fontWeight: 800,
};

const secondaryButtonStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #35506b",
  background: "#142033",
  color: "#dcecff",
  cursor: "pointer",
  fontWeight: 800,
};

const dangerButtonStyle = {
  background: "#5a1f1f",
  color: "#fff",
  border: "1px solid #7a2e2e",
  borderRadius: 10,
  padding: "12px 16px",
  cursor: "pointer",
  fontWeight: 800,
};

const targetMiniButtonStyle = {
  textAlign: "left",
  width: "100%",
  border: "1px solid #2f4052",
  background: "#111923",
  color: "#fff",
  borderRadius: 12,
  padding: 14,
  cursor: "pointer",
};
